import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  LogOut,
  Calendar as CalendarIcon,
  Clock,
  User,
  Trash2,
  Edit3,
  Plus,
  Search,
  History,
  CalendarDays,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Check,
  X as XIcon,
  Sparkles,
  Repeat,
  Pill,
  Copy,
  ClipboardPaste,
  FileCheck2,
  AlertCircle,
  IdCard,
  Users as UsersIcon,
  Receipt,
  DollarSign,
  Share2,
  HelpCircle,
  RefreshCw,
  PauseCircle,
  UserPlus,
  Package,
  Droplets,
  Boxes,
  Megaphone,
  ClipboardList,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";

import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

import { api } from "@/lib/consult/api";
import { clearToken, isAuthed } from "@/lib/consult/authSession";
import { TimePicker15 } from "@/components/consult/TimePicker15";
import OutreachPanel from "@/pages/consult/OutreachPanel";
import InventoryPanel from "@/pages/consult/InventoryPanel";
import InvoicingPanel from "@/pages/consult/InvoicingPanel";
import IncentivesPanel from "@/pages/consult/IncentivesPanel";
import PharmacyPanel from "@/pages/consult/PharmacyPanel";
import ReferralsPanel from "@/pages/consult/ReferralsPanel";
import BloodworkPanel from "@/pages/consult/BloodworkPanel";
import LockerPanel from "@/pages/consult/LockerPanel";
import SurveyPanel from "@/pages/consult/SurveyPanel";import { parsePatientPaste } from "@/pages/consult/patientPaste";
import ExpensesPanel from "@/pages/consult/ExpensesPanel";
import MarketingPanel from "@/pages/consult/MarketingPanel";
import axios from "axios";

const DARK_GREEN = "#0F3B2E";
const LABEL_GREEN = "#2D5A4C";
const TEXT = "#1C2B26";
const MUTED = "#6B7A73";
const BG = "#F4F3EF";
const BORDER = "#E5E2DC";
const COMPLETED_COL = "#2E7D5B";
const MISSED_COL = "#B4351B";
const NEW_COL = "#0F3B2E";
const RESCHED_COL = "#8A5A00";
const REF_PENDING_COL = "#B4351B";
const NOT_REQUIRED_COL = "#8B7A5C";  // warm taupe — reads as "optional / not needed"

const STAFF_NAMES = [
  "George Holness",
  "Malachi Whyte",
  "Racquel Anthony",
  "Manitoba",
  "Christian Marcoux",
];
// Fallback short labels until the shared staff/team registry loads.
const STAFF_SHORTS = ["George", "Malachi", "Racquel", "Manitoba", "Christian"];
// Per-staff chart/legend colours — the first five match the original
// hard-coded colours; additional staff cycle through the rest.
const STAFF_PALETTE = [DARK_GREEN, "#4A6FB0", COMPLETED_COL, "#C26E3D", NEW_COL, "#8A5A00", "#7A4FA3", "#0E7C7B", "#C77D26", MISSED_COL];

// ---------------------- date helpers ----------------------
const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const fromYMD = (ymd) => {
  const [y, m, d] = (ymd || "").split("-").map(Number);
  // Never return an Invalid Date — date-fns format() throws "Invalid time
  // value" on one and crashes the whole render. Fall back to today.
  if (!y || !m || !d) return new Date();
  return new Date(y, m - 1, d);
};

const prettyDate = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return "—";
  return format(fromYMD(ymd), "EEEE, MMM d, yyyy");
};
const prettyDateShort = (ymd) => {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}/.test(ymd)) return "—";
  return format(fromYMD(ymd), "MMM d, yyyy");
};

// Chronological color palette for registration-month segments. Cycled from
// oldest → newest so the "current" period lands on the darkest green (matches
// the design language on the rest of the dashboard).
const REG_MONTH_PALETTE = [
  "#C7B98F", // sand
  "#8FA69A", // sage
  "#5F8A76", // muted teal
  "#2E7D5B", // completed green
  "#0F3B2E", // brand dark green (most recent)
];

// Build BreakdownBar segments from `stats.registration_months`. Handles the
// empty/undefined case gracefully.
const buildRegMonthSegments = (regMonths) => {
  if (!regMonths || regMonths.length === 0) return [];
  return regMonths.map((rm, i) => {
    // Compute reg_from/reg_to bounds for the drill-down filter
    let regFrom = "", regTo = "";
    if (rm.month && /^\d{4}-\d{2}$/.test(rm.month)) {
      const [y, m] = rm.month.split("-").map(Number);
      regFrom = `${y}-${String(m).padStart(2, "0")}-01`;
      const nextY = m === 12 ? y + 1 : y;
      const nextM = m === 12 ? 1 : m + 1;
      regTo = `${nextY}-${String(nextM).padStart(2, "0")}-01`;
    }
    const label = /^\d{4}-\d{2}$/.test(rm.month)
      ? format(new Date(rm.month + "-01"), "MMM yyyy")
      : "Unknown";
    // Assign palette colours so the LATEST month gets the darkest colour.
    const color = REG_MONTH_PALETTE[
      Math.max(0, REG_MONTH_PALETTE.length - regMonths.length + i)
    ] || "#4A7C9B";
    return {
      label,
      count: rm.count,
      pct: rm.percentage,
      color,
      filter: regFrom ? { reg_from: regFrom, reg_to: regTo } : {},
    };
  });
};

// Palette for the Medications bar — reuses distinct hues so each
// medication stays visually stable across renders (name → colour lookup).
const MED_PALETTE = ["#0F3B2E", "#2E7D5B", "#4A7C9B", "#4A6FB0", "#E58A2E", "#B4351B", "#6B7A73", "#9C6BB8"];
const buildMedicationSegments = (meds, regScope = null, extras = null) => {
  const base = meds && meds.length > 0
    ? meds.map((m, i) => ({
        label: m.name,
        count: m.count,
        pct: m.percentage,
        color: MED_PALETTE[i % MED_PALETTE.length],
        filter: { medication: m.name, ...(regScope || {}) },
      }))
    : [];
  // Append Hold + Not qualified pseudo-segments so they stay visible on
  // the Medications chart without inflating any real medication bucket.
  // The moment a record's status flips off hold/not-qualified the next
  // stats fetch will correctly move it back into its medication segment.
  if (extras?.hold && extras.hold.count > 0) {
    base.push({
      label: "Hold",
      count: extras.hold.count,
      pct: extras.hold.pct,
      color: "#E58A2E",
      filter: { status: "hold", ...(regScope || {}) },
    });
  }
  if (extras?.notQualified && extras.notQualified.count > 0) {
    base.push({
      label: "Not qualified",
      count: extras.notQualified.count,
      pct: extras.notQualified.pct,
      color: "#B4351B",
      filter: { status: "not_qualified", ...(regScope || {}) },
    });
  }
  if (extras?.replacement && extras.replacement.count > 0) {
    base.push({
      label: "Replacement",
      count: extras.replacement.count,
      pct: extras.replacement.pct,
      color: "#4A6FB0",
      filter: { client_type: "replacement", ...(regScope || {}) },
    });
  }
  return base;
};

// Ontario HCN format: 10 digits, a space, then 2 letters (upper-cased).
// The 2-letter version code is optional — when the source lacks it we
// return just the digits (no trailing space) so the field stays clean.
const formatHCN = (raw) => {
  const cleaned = (raw || "").toUpperCase().replace(/[^0-9A-Z]/g, "");
  const digits = cleaned.replace(/[A-Z]/g, "").slice(0, 10);
  const letters = cleaned.slice(digits.length).replace(/[0-9]/g, "").slice(0, 2);
  if (digits.length < 10) return digits;
  return letters ? `${digits} ${letters}` : digits;
};

// Parse a patient clipboard payload into { patient_name?, hcn?, dob? }.
// Handles multiple label styles because different registries copy in
// different shapes:
//
//   Shape A ("Copy RE / PHN / DOB" from this same modal):
//     RE: John Smith
//     PHN: 1234567890 XX
//     DOB: Apr 18, 1991
//
//   Shape B (Ontario EMR export, screenshot 2026-08-06):
//     HCN: 7823245936 JD Sex: M       ← two labels on one line
//     Leduc, Joel                     ← name as "Last, First", no label
//     DOB: 1979-08-26
//     Address: 256 Murray St          ← ignored
//     Ottawa, ON K1N 5M9              ← ignored (postal code line)
//     Phone number not available      ← ignored
//     2026-08-06 8:37 AM              ← ignored (timestamp)
//
// Case-insensitive, forgiving on whitespace, strips zero-width unicode
// (U+200B) that some copy tools leave before the colon.


// Copy plain text to clipboard. Uses an explicit `text/plain` MIME so mobile
// paste destinations (esp. iOS Safari + Messages) don't URL-encode content
// that starts with `RE:` or contains newlines.
const copyPlainText = async (text) => {
  // Prefer the modern ClipboardItem API with explicit MIME type — this is
  // the only method that reliably paints "text/plain" onto the iOS clipboard.
  try {
    if (
      typeof window !== "undefined" &&
      window.ClipboardItem &&
      navigator.clipboard &&
      navigator.clipboard.write
    ) {
      const blob = new Blob([text], { type: "text/plain" });
      const item = new window.ClipboardItem({ "text/plain": blob });
      await navigator.clipboard.write([item]);
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  // Fallback 1: writeText — works on most desktop browsers.
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    /* fall through */
  }
  // Fallback 2: hidden textarea + execCommand("copy") — legacy fallback.
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "absolute";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (_) {
    return false;
  }
};

const prettyTime = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return "—";
  const [h, m] = hhmm.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

// Convert HH:MM to total minutes for comparison / sorting.
const toMins = (hhmm) => {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
};

// Case-management medication pipeline stages (dropdown order = pipeline
// order). A record counts only in its FURTHEST stage in Stats; "Please
// select" (no flag set) lands in Pending.
const MED_STAGES = [
  { v: "refused", label: "Refused", col: "#717D7E" },
  { v: "external_tx", label: "External Tx", col: "#CA6F1E" },
  { v: "no_coverage", label: "No Coverage", col: "#566573" },
  { v: "bloodwork", label: "Bloodwork", col: "#C0392B" },
  { v: "genotype", label: "Genotype", col: "#00838F" },
  { v: "resistance", label: "Resistance", col: "#2E86C1" },
  { v: "consultation", label: "Consultation", col: "#4E9A51" },
  { v: "prescription", label: "Prescription", col: "#B4436C" },
  { v: "reimbursement", label: "Reimbursement", col: "#9C6644" },
  { v: "compassionate", label: "Compassionate", col: "#0E7490" },
  { v: "submitted", label: "Submitted", col: "#4A6FB0" },
  { v: "manual_claim", label: "Manual claim", col: "#B7950B" },
  { v: "pharmacy", label: "Pharmacy", col: "#6C3483" },
  { v: "dispensed", label: "Dispensed", col: "#2A9F92" },
  { v: "delivery", label: "Delivery", col: "#884EA0" },
];

// Fallback list for the case-management dropdown; the live list is managed
// server-side (consult_case_stages) and can be edited from the dialog.
const DEFAULT_CASE_STAGES = MED_STAGES.map((s) => ({ key: s.v, label: s.label, color: s.col }));

// Stats card case-management groups (always expanded). Each managed stage's
// `group` field (set from the Manage dialog) places it under one of these;
// custom stages default to Active. Display-only — pipeline order still
// drives the furthest-stage counts.
const CASE_STAGE_GROUPS = [
  { key: "active", label: "Active", color: "#4E9A51" },
  { key: "not_qualified", label: "Not qualified", color: "#B4351B" },
  { key: "reimbursement", label: "Reimbursement", color: "#9C6644" },
  { key: "pharmacy", label: "Pharmacy", color: "#6C3483" },
];

// ---------------------- root component ----------------------
export default function ConsultDashboard() {
  const nav = useNavigate();

  // Tab state doubles as browser history: every section switch pushes a
  // masked history entry (address bar stays "/"), so Back/Forward step
  // through the sections you visited instead of exiting to the main site.
  const [tab, setTabState] = useState(() => {
    try { return sessionStorage.getItem("sched_tab") || "day"; } catch { return "day"; }
  }); // day | history | stats | outreach | inventory | invoicing | ...
  const tabRef = useRef(tab);
  const setTab = useCallback((t) => {
    if (t === tabRef.current) return;  // no duplicate entries for the active tab
    tabRef.current = t;
    try { sessionStorage.setItem("sched_tab", t); } catch { /* ignore */ }
    // NOTE: pushState lives OUTSIDE any setState updater — StrictMode
    // double-invokes updaters in dev, which used to push each entry twice.
    try { window.history.pushState({ appPath: "/outreach/dashboard", schedTab: t }, "", "/"); } catch { /* ignore */ }
    setTabState(t);
  }, []);
  // Stamp the initial tab onto the current entry + honor Back/Forward.
  useEffect(() => {
    try { window.history.replaceState({ ...(window.history.state || {}), schedTab: tabRef.current }, "", "/"); } catch { /* ignore */ }
    const onPop = (e) => {
      const t = e.state?.schedTab;
      if (t) {
        tabRef.current = t;
        try { sessionStorage.setItem("sched_tab", t); } catch { /* ignore */ }
        setTabState(t);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  const [perms, setPerms] = useState({ invoicing: false, outreach_edit: false, mode: "user", staff_name: null, staff_team: null });

  // ── staff-scope PREVIEW (review only, client-side): open the dashboard with
  // ?preview_staff=George%20Holness to see exactly what that staff member's
  // own PIN login will see. Normal usage is untouched. NOTE: reads the router
  // location — App.js masks window.location to "/".
  const location = useLocation();
  const previewStaff = useMemo(
    () => new URLSearchParams(location.search).get("preview_staff"), [location.search]);
  const previewFirst = previewStaff ? previewStaff.split(" ")[0] : null;
  const PREVIEW_TEAMS = { George: "team_1", Racquel: "team_2", Malachi: "team_3" };
  // staff scope: real staff logins carry staff_name/team on their user account;
  // admins can review the same view via ?preview_staff=… (browser-only).
  const staffScope = previewStaff || (perms.mode === "user" ? perms.staff_name : null);
  const staffScopeFirst = staffScope ? staffScope.split(" ")[0] : null;
  const staffScopeTeam = previewFirst ? (PREVIEW_TEAMS[previewFirst] || null) : (perms.mode === "user" ? perms.staff_team : null);
  // Ready-made label ("Team 4 (M)") so locked panels can paint their team
  // pill before the registry fetch lands.
  const staffScopeTeamLabel = staffScopeTeam
    ? `Team ${staffScopeTeam.replace("team_", "")} (${(staffScopeFirst || "?").charAt(0)})`
    : null;
  // Tab-level access (set per user in Users admin → Section access). When no
  // tab_* flag is set the user sees every tab (legacy behaviour); once any is
  // set, only the checked tabs render. Invoicing always stays opt-in.
  const TAB_ORDER = ["day", "history", "pharmacy", "outreach", "inventory", "incentives", "referrals", "expenses", "stats", "locker", "marketing", "bloodwork", "survey", "invoicing"];
  const TAB_PERM_KEY = { day: "tab_md", history: "tab_all", pharmacy: "tab_pharmacy", outreach: "tab_calendar", inventory: "tab_inventory", incentives: "tab_rewards", referrals: "tab_referrals", expenses: "tab_expenses", stats: "tab_stats", locker: "tab_locker", marketing: "tab_marketing", bloodwork: "tab_bloodwork", survey: "tab_survey", invoicing: "invoicing" };
  const anyTabSet = perms.mode === "user" && TAB_ORDER.some((t) => TAB_PERM_KEY[t] !== "invoicing" && perms[TAB_PERM_KEY[t]]);
  const tabAllowed = (t) => {
    if (perms.mode !== "user") return true; // master login / admin preview sees all
    const key = TAB_PERM_KEY[t];
    if (key === "invoicing") return !!perms.invoicing;
    if (!anyTabSet) return true;
    return !!perms[key];
  };
  const tabHiddenByScope = (t) => !!(staffScope && (t === "history" || t === "referrals"));
  const firstAllowedTab = TAB_ORDER.find((t) => tabAllowed(t) && !tabHiddenByScope(t)) || "day";
  // The name search is only useful where client records/rewards live — an
  // account limited to sections like Expenses or Inventory never needs it,
  // so the bar stays hidden for them.
  const SEARCH_TABS = ["day", "history", "pharmacy", "stats", "incentives"];
  const showGlobalSearch = perms.mode !== "user" || SEARCH_TABS.some((t) => tabAllowed(t));
  // Staff logins: the All-appointments list and Referrals are admin-only, and any
  // tab the account isn't granted bounces to the first tab it can see.
  useEffect(() => {
    if ((!tabAllowed(tab) || tabHiddenByScope(tab)) && tab !== firstAllowedTab) setTab(firstAllowedTab);
  }, [staffScope, tab, perms]);
  // Global "refresh" counter — bumped by the toolbar Refresh button so
  // every panel that watches it (OutreachPanel, InvoicingPanel) can
  // re-fetch without a full page reload.
  const [refreshKey, setRefreshKey] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [availabilityMap, setAvailabilityMap] = useState({});
  const [day, setDay] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);

  // Global patient search
  const [globalQ, setGlobalQ] = useState("");
  const [globalResults, setGlobalResults] = useState([]);
  const [globalOpen, setGlobalOpen] = useState(false);
  const globalRef = useRef(null);
  const globalInputRef = useRef(null);
  const globalTimerRef = useRef(null);

  // Booking dialog
  const [slotDialog, setSlotDialog] = useState(null); // { slot_time, appointment? }
  const [formName, setFormName] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formClientType, setFormClientType] = useState("new");
  const [formReferralType, setFormReferralType] = useState("outreach");
  const [formStatus, setFormStatus] = useState("scheduled");
  const [formScheduledBy, setFormScheduledBy] = useState("");
  const [formStaffTouched, setFormStaffTouched] = useState(false);
  const [formReferralDone, setFormReferralDone] = useState("review"); // four-state: review (default) | not_completed | completed | not_required
  const [formHealthCard, setFormHealthCard] = useState("no"); // "yes" | "no" | "recovered"
  const [formDOB, setFormDOB] = useState("");        // YYYY-MM-DD or ""
  const [formHCN, setFormHCN] = useState("");        // Health Card Number
  const [formRegDate, setFormRegDate] = useState(""); // registration_date
  const [formRecoveredDate, setFormRecoveredDate] = useState(""); // recovered_date
  const [formMedication, setFormMedication] = useState("");
  const [formRefill, setFormRefill] = useState(false);
  const [formRefillDate, setFormRefillDate] = useState("");
  const [refillDateOpen, setRefillDateOpen] = useState(false);
  // Case-management stage flags — one object keyed by stage key, driven by the
  // managed list (server-side, editable from the dialog's Manage link).
  const [caseStages, setCaseStages] = useState(DEFAULT_CASE_STAGES);
  const [formStages, setFormStages] = useState({});
  // Furthest ticked stage (LAST in the managed list order) drives the
  // dropdown. Fully decoupled from the record STATUS — "Not qualified" is
  // set from the Status buttons (with its reason dropdown), never from here.
  const medStageValue = (() => {
    for (let i = caseStages.length - 1; i >= 0; i--) {
      if (formStages[caseStages[i].key]) return caseStages[i].key;
    }
    return "__none__";
  })();
  const setMedStage = (stage) => {
    // Stage selection never touches the record status.
    setFormStages(Object.fromEntries(caseStages.map((s) => [s.key, s.key === stage])));
  };
  // A drug choice only becomes mandatory from the Prescription stage onward —
  // bloodwork and consultation legitimately happen before any medication.
  const medStageNeedsDrug = ["prescription", "reimbursement", "compassionate", "submitted", "manual_claim", "delivery", "pharmacy", "dispensed"].includes(medStageValue);
  const [formReplacementMonths, setFormReplacementMonths] = useState(0); // 0 | 1 | 2 | 3
  const [formReason, setFormReason] = useState(""); // reason label for hold/not_qualified
  const [medications, setMedications] = useState([]);
  const [manageMedsOpen, setManageMedsOpen] = useState(false);
  // Reasons picklists — separate for hold vs not_qualified.
  const [holdReasons, setHoldReasons] = useState([]);
  const [nqReasons, setNqReasons] = useState([]);
  const [manageReasonsFor, setManageReasonsFor] = useState(null); // "hold" | "not_qualified" | null
  const [dobOpen, setDobOpen] = useState(false);
  const [regDateOpen, setRegDateOpen] = useState(false);
  const [recoveredDateOpen, setRecoveredDateOpen] = useState(false);
  // Reschedule (edit mode only): target date + time + available slots list
  const [formApptDate, setFormApptDate] = useState("");   // YYYY-MM-DD
  const [formApptSlot, setFormApptSlot] = useState("");   // HH:MM
  const [apptDateOpen, setApptDateOpen] = useState(false);
  const [rescheduleSlots, setRescheduleSlots] = useState([]); // ["09:00","09:15",...]
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [savingSlot, setSavingSlot] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // History
  const [historyItems, setHistoryItems] = useState([]);
  const [historyQ, setHistoryQ] = useState("");
  const [historyMode, setHistoryMode] = useState("all");
  const [historyStatus, setHistoryStatus] = useState("all");
  const [historyReferral, setHistoryReferral] = useState("all");
  const [historyReferralSource, setHistoryReferralSource] = useState("all");
  const [historyShowHolds, setHistoryShowHolds] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Stats
  const [statsMonth, setStatsMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  // Custom date range for stats — when set, overrides the month picker.
  const [statsRange, setStatsRange] = useState(null); // { from, to } | null

  // Stats "drill-down" — clicking a bar/legend opens a filtered list of
  // appointments for the current statsMonth. Each row jumps to the appt.
  const [statsDetail, setStatsDetail] = useState(null); // { label, params, items, loading }
  // When the user opens an appointment FROM the stats drill-down, we stash
  // the drill-down state here so we can restore it after they close the
  // slot dialog (they land back on stats, not on the day view).
  const [statsDetailReturn, setStatsDetailReturn] = useState(null);

  useEffect(() => {
    if (!isAuthed()) nav("/outreach", { replace: true });
  }, [nav]);

  // Load current session permissions to gate the Invoicing tab.
  useEffect(() => {
    (async () => {
      try {
        const token = (typeof window !== "undefined")
          ? window.sessionStorage.getItem("pp_auth_token") : null;
        if (!token) return;
        const backend = process.env.REACT_APP_BACKEND_URL;
        const { data } = await axios.get(`${backend}/api/pp/auth/whoami`, {
          headers: { "X-Auth-Token": token },
        });
        setPerms({
          mode: data?.mode || "user",
          isAdmin: data?.mode === "master" || !!(data?.permissions?.users_admin),
          ...(data?.permissions || {}),
          invoicing: data?.mode === "master" || !!(data?.permissions?.invoicing),
          outreach_edit: data?.mode === "master" || !!(data?.permissions?.outreach_edit),
          staff_name: data?.user?.staff_name || null,
          staff_team: data?.user?.staff_team || null,
        });
      } catch { /* ignore, defaults to no invoicing */ }
    })();
  }, []);

  // Live staff list from the shared registry — staff added in Users admin
  // flow into the booking dropdown, Calendar and Stats automatically.
  const [staffList, setStaffList] = useState(null);
  // MD tab staff pill — admin's default "Scheduled by" for new bookings.
  const [mdStaff, setMdStaff] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/staff-teams");
        if (Array.isArray(data?.staff) && data.staff.length) setStaffList(data.staff);
      } catch { /* keep fallbacks */ }
    })();
  }, []);
  const staffNames = staffList ? staffList.map((s) => s.full) : STAFF_NAMES;
  const staffShorts = staffList ? staffList.map((s) => s.short) : STAFF_SHORTS;
  // Staff appear in every section only from their start month onward (empty
  // start month = all history). `keep` always survives the filter so the
  // value already saved on an open record never blanks out.
  const staffNamesForMonth = (monthKey, keep = "") => {
    if (!staffList) return STAFF_NAMES;
    return staffList
      .filter((s) => !s.start_month || s.start_month <= monthKey || s.full === keep)
      .map((s) => s.full);
  };

  const selectedYMD = toYMD(selectedDate);

  const loadAvailability = useCallback(async () => {
    try {
      const { data } = await api.get("/availability");
      const map = {};
      for (const a of data) map[a.date] = a;
      setAvailabilityMap(map);
    } catch { /* handled */ }
  }, []);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const loadDay = useCallback(async (ymd) => {
    setLoadingDay(true);
    try {
      const { data } = await api.get(`/day/${ymd}`);
      setDay(data);
    } catch {
      setDay(null);
    } finally {
      setLoadingDay(false);
    }
  }, []);

  useEffect(() => { loadDay(selectedYMD); }, [selectedYMD, loadDay]);

  // Load medications once so the Medication dropdown is populated.
  const loadMedications = useCallback(async () => {
    try {
      const { data } = await api.get("/medications");
      setMedications(data?.items || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadMedications(); }, [loadMedications, refreshKey]);

  // Managed case-management stages (compassionate + user edits come from here).
  const [manageStagesOpen, setManageStagesOpen] = useState(false);
  const loadCaseStages = useCallback(async () => {
    try {
      const { data } = await api.get("/case-stages");
      if (data?.stages?.length) setCaseStages(data.stages);
    } catch { /* keep the built-in defaults */ }
  }, []);
  useEffect(() => { loadCaseStages(); }, [loadCaseStages, refreshKey]);

  // Reasons for Hold + Not-qualified statuses.
  const loadReasons = useCallback(async () => {
    try {
      const [h, n] = await Promise.all([
        api.get("/reasons", { params: { type: "hold" } }),
        api.get("/reasons", { params: { type: "not_qualified" } }),
      ]);
      setHoldReasons(h?.data?.items || []);
      setNqReasons(n?.data?.items || []);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { loadReasons(); }, [loadReasons, refreshKey]);

  // ---------- global search (debounced) ----------
  // Context-aware: on the Incentives tab we search incentive records
  // (name of person who received a payout). Everywhere else we search
  // doctor appointments as before.
  useEffect(() => {
    const q = globalQ.trim();
    if (!q) {
      setGlobalResults([]);
      setGlobalOpen(false);
      return;
    }
    if (globalTimerRef.current) clearTimeout(globalTimerRef.current);
    globalTimerRef.current = setTimeout(async () => {
      try {
        if (tab === "incentives") {
          const { data } = await api.get("/incentives/records", { params: { q } });
          const items = (data?.items || []).slice(0, 12).map((r) => ({
            ...r, __kind: "incentive",
          }));
          setGlobalResults(items);
          setGlobalOpen(true);
        } else {
          const { data } = await api.get("/appointments", { params: { q } });
          setGlobalResults((data || []).slice(0, 12).map((a) => ({
            ...a, __kind: "appointment",
          })));
          setGlobalOpen(true);
        }
      } catch { /* ignore */ }
    }, 220);
    return () => globalTimerRef.current && clearTimeout(globalTimerRef.current);
  }, [globalQ, tab]);

  // Re-run the search when the user toggles between tabs while a query
  // is active — otherwise stale results from the other data source stay
  // in the dropdown.
  useEffect(() => {
    if (globalQ.trim()) {
      // Force a refetch by no-op setting the same value (effect above depends on tab).
      // No action needed — the effect above already depends on `tab`.
    } else {
      setGlobalResults([]);
      setGlobalOpen(false);
    }
  }, [tab]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (globalRef.current && !globalRef.current.contains(e.target)) {
        setGlobalOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const jumpToAppt = (appt) => {
    setGlobalOpen(false);
    setGlobalQ("");
    // Hold / not-qualified records have no date or slot — open them via the
    // __hold path (same as the All-appointments panel). Navigating to an
    // empty date crashed the day view with "Invalid time value".
    if (appt.status === "hold" || appt.status === "not_qualified" || !appt.date) {
      setTab("day");
      setTimeout(() => openSlot({ __hold: true, slot_time: "", appointment: appt }), 220);
      return;
    }
    setSelectedDate(fromYMD(appt.date));
    setTab("day");
    // Open the slot dialog once the day has (re)loaded.
    setTimeout(() => openSlot({ slot_time: appt.slot_time, appointment: appt }), 260);
  };

  // Incentives: request the panel to open the given record for editing.
  // We pass an incrementing (id, nonce) tuple so back-to-back clicks on
  // the same record still re-trigger the effect inside the panel.
  const [incentiveOpenReq, setIncentiveOpenReq] = useState(null);
  const jumpToIncentive = (rec) => {
    setTab("incentives");
    setGlobalOpen(false);
    setGlobalQ("");
    setIncentiveOpenReq({ record: rec, nonce: Date.now() });
  };

  // Open an appointment straight from the stats drill-down list WITHOUT
  // navigating away from the Stats tab. When the user closes the slot
  // dialog we restore the drill-down modal so they land right back where
  // they were (see closeSlot below).
  const openApptFromStats = (appt) => {
    setStatsDetailReturn(statsDetail);
    setStatsDetail(null); // hide drill-down while the slot dialog is open
    // Hold / not-qualified records have no slot — open via the __hold path.
    if (appt.status === "hold" || appt.status === "not_qualified" || !appt.date) {
      openSlot({ __hold: true, slot_time: "", appointment: appt });
      return;
    }
    openSlot({ slot_time: appt.slot_time, appointment: appt });
  };

  // Compute month range and open the stats-detail dialog with filtered results.
  //
  // Guarded against overlapping clicks: each call carries a request-id
  // stashed on the panel state, and only the newest response is applied.
  // If a request throws, we surface the actual server error and one-shot
  // retry via a Sonner toast (rather than a bare "Failed to load…").
  const statsReqIdRef = useRef(0);
  const openStatsDetail = async (label, params) => {
    let first, last;
    if (statsRange) {
      first = statsRange.from;
      last  = statsRange.to;
    } else {
      const [y, m] = statsMonth.split("-").map(Number);
      first = `${y}-${String(m).padStart(2, "0")}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      last  = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
    }
    // Strip client-only markers before sending, and drop the appt-date
    // window when this drill-down is scoped by registration date instead
    // (Individual breakdown chip). `_exclude_holds_and_replacements` is a
    // client-side post-filter used by the RegPerDay chart's Scheduled
    // segment (backend has no equivalent single query). Mirrors the chart's
    // "scheduled" bar: excludes hold, not-qualified and replacement records.
    const { _reg_scope, _exclude_holds_and_replacements, _row_mode, ...cleanParams } = params || {};
    const reqParams = _reg_scope
      ? { ...cleanParams }
      : { from: first, to: last, ...cleanParams };

    const myId = ++statsReqIdRef.current;
    setStatsDetail({ label, params, items: [], loading: true, reqId: myId });

    const doFetch = () => api.get("/appointments", { params: reqParams, timeout: 20000 });
    const applyItems = (data) => {
      // Only apply if this is still the latest request the user asked for.
      if (statsReqIdRef.current !== myId) return;
      let items = data || [];
      if (_exclude_holds_and_replacements) {
        items = items.filter((a) =>
          (a.status || "") !== "hold" && (a.status || "") !== "not_qualified" && (a.client_type || "") !== "replacement"
        );
      }
      setStatsDetail({ label, params, items, loading: false, reqId: myId });
    };

    try {
      const { data } = await doFetch();
      applyItems(data);
    } catch (err) {
      // Stale request — a newer click has superseded us. Stay silent.
      if (statsReqIdRef.current !== myId) return;
      // One transient retry (network blips / cold-pod warmup on prod).
      try {
        await new Promise((r) => setTimeout(r, 400));
        if (statsReqIdRef.current !== myId) return;
        const { data } = await doFetch();
        applyItems(data);
        return;
      } catch (err2) {
        if (statsReqIdRef.current !== myId) return;
        const detail = err2?.response?.data?.detail
          || err2?.response?.statusText
          || err2?.message
          || "Unknown error";
        const code = err2?.response?.status ? ` (HTTP ${err2.response.status})` : "";
        toast.error(`Failed to load appointments: ${detail}${code}`);
        setStatsDetail(null);
      }
    }
  };
  const closeStatsDetail = () => setStatsDetail(null);

  // ---------- history ----------
  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const params = {};
      const today = toYMD(new Date());
      if (historyMode === "past") params.to = today;
      if (historyMode === "upcoming") params.from = today;
      if (historyQ.trim()) params.q = historyQ.trim();
      if (historyStatus !== "all") params.status = historyStatus;
      if (historyReferral !== "all") params.referral = historyReferral;
      if (historyReferralSource !== "all") params.referral_source = historyReferralSource;
      if (historyShowHolds) params.include_holds = 1;
      const { data } = await api.get("/appointments", { params });
      setHistoryItems(data);
    } catch {
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [historyMode, historyQ, historyStatus, historyReferral, historyReferralSource, historyShowHolds]);

  useEffect(() => {
    if (tab === "history") loadHistory();
  }, [tab, loadHistory]);

  // ---------- stats ----------
  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const params = statsRange
        ? { from: statsRange.from, to: statsRange.to }
        : { month: statsMonth };
      const { data } = await api.get("/stats", { params });
      setStats(data);
    } catch {
      setStats(null);
    } finally {
      setStatsLoading(false);
    }
  }, [statsMonth, statsRange]);

  useEffect(() => {
    if (tab === "stats") loadStats();
  }, [tab, loadStats]);

  // ---------- logout ----------
  const doLogout = () => {
    clearToken();
    nav("/outreach", { replace: true });
  };

  // ---------- refresh ----------
  // Reload every dashboard surface without a full page refresh. Staff
  // often need the latest appointments/stats while mid-booking, and a
  // browser reload forces them to re-authenticate. This button reloads
  // MD Schedule (day + availability), History (All appointments), Stats,
  // and — via the refreshKey bump — Outreach + Invoicing panels.
  const doRefresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        loadAvailability(),
        loadDay(selectedYMD),
        loadHistory().catch(() => {}),
        loadStats().catch(() => {}),
      ]);
      setRefreshKey((k) => k + 1);
      // The Survey tab keeps its own filters (city/location/month/staff,
      // search, tile/payment picks) — refresh resets all of them too.
      window.dispatchEvent(new CustomEvent("survey:reset"));
      toast.success("Refreshed.");
    } catch {
      toast.error("Refresh failed.");
    } finally {
      setRefreshing(false);
    }
  }, [loadAvailability, loadDay, loadHistory, loadStats, selectedYMD]);

  // ---------- availability: read-only chips + instant add/remove ----------
  // No whole-day "save": windows are added/removed one at a time directly on
  // the server, so the day can never be wiped by a stale form and a booked
  // patient can never be stranded by an accidental edit.
  const currentWindows = day?.availability?.windows || [];
  const [savingAvail, setSavingAvail] = useState(false);
  const [newWindow, setNewWindow] = useState({ start_time: "09:00", end_time: "10:00" });

  // Suggest a sensible next window whenever the day (re)loads: one hour
  // after the last existing window ends, or 9–10 AM for an empty day.
  useEffect(() => {
    if (!day || day.date !== selectedYMD) return;
    const fmt = (t) => `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
    if (currentWindows.length) {
      const lastEnd = Math.max(...currentWindows.map((w) => toMins(w.end_time)));
      const s = Math.min(lastEnd + 60, 22 * 60);
      setNewWindow({ start_time: fmt(s), end_time: fmt(Math.min(s + 60, 23 * 60 + 45)) });
    } else {
      setNewWindow({ start_time: "09:00", end_time: "10:00" });
    }
  }, [selectedYMD, day]);

  // Booked-slot count per window — shown on the chip so it's visible WHY a
  // window with patients can't be removed.
  const bookedCountInWindow = (w) => {
    const s = toMins(w.start_time);
    const e = toMins(w.end_time);
    return (day?.appointments || []).filter((a) => {
      const m = toMins(a.slot_time || "00:00");
      return m >= s && m < e;
    }).length;
  };

  const addAvailabilityWindow = async () => {
    if (loadingDay || !day || day.date !== selectedYMD) {
      toast.error("This day is still loading — try again in a moment.");
      return;
    }
    if (!newWindow.start_time || !newWindow.end_time || toMins(newWindow.start_time) >= toMins(newWindow.end_time)) {
      toast.error("Pick an end time after the start time.");
      return;
    }
    setSavingAvail(true);
    try {
      await api.post("/availability/add", { date: selectedYMD, windows: [newWindow] });
      toast.success("Time window added.");
      await Promise.all([loadDay(selectedYMD), loadAvailability()]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error((typeof d === "string" ? d : d?.message) || "Failed to add the window.");
    } finally {
      setSavingAvail(false);
    }
  };

  const removeAvailabilityWindow = async (w) => {
    if (!window.confirm(`Remove the ${prettyTime(w.start_time)} – ${prettyTime(w.end_time)} window?`)) return;
    setSavingAvail(true);
    try {
      await api.post("/availability/remove", {
        date: selectedYMD,
        windows: [{ start_time: w.start_time, end_time: w.end_time }],
      });
      toast.success("Time window removed.");
      await Promise.all([loadDay(selectedYMD), loadAvailability()]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "slots_have_patients") {
        const conflicts = d.conflicts || [];
        const names = conflicts.slice(0, 3)
          .map((c) => `${c.patient_name} (${prettyTime(c.slot_time)})`)
          .join(", ");
        const extra = conflicts.length > 3 ? ` +${conflicts.length - 3} more` : "";
        toast.error(`Can't remove a window that has a scheduled patient: ${names}${extra}. Delete or move the patient first.`);
      } else {
        toast.error((typeof d === "string" ? d : d?.message) || "Failed to remove the window.");
      }
    } finally {
      setSavingAvail(false);
    }
  };

  const clearAvailability = async () => {
    if (!currentWindows.length) return;
    if (!window.confirm("Clear the doctor's availability for this day?")) return;
    try {
      await api.delete(`/availability/${selectedYMD}`);
      toast.success("Availability cleared.");
      await Promise.all([loadDay(selectedYMD), loadAvailability()]);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409 && d?.code === "slots_have_patients") {
        toast.error("This day still has scheduled patients — delete or move each patient first.");
      } else {
        toast.error("Failed to clear availability.");
      }
    }
  };

  // ---------- slot dialog ----------
  const openSlot = (slot) => {
    setSlotDialog(slot);
    const a = slot.appointment;
    setFormName(a?.patient_name || "");
    setFormNotes(a?.notes || "");
    setFormClientType(a?.client_type || "new");
    setFormReferralType(a ? (a.referral_type || "") : "outreach");
    setFormStatus(a?.status || (slot.__hold ? "hold" : slot.__new ? "completed" : "scheduled"));
    setFormScheduledBy(a?.scheduled_by || staffScope || mdStaff || "");
    setFormReferralDone(
      // Normalize legacy bool → tri-state string. Holds are always
      // "not required" for the referral to specialist.
      slot.__hold
        ? "not_required"
        : typeof a?.referral_completed === "string"
          ? a.referral_completed
          // Existing legacy record (bool/missing) -> Pending; brand-new slot
          // -> Review; New (real-time intake) -> Yes (referral completed).
          : a
            ? (a.referral_completed === true ? "completed" : "not_completed")
            : slot.__new
              ? "completed"
              : "review"
    );
    setFormMedication(a?.medication || "");
    setFormRefill(!!a?.refill);
    setFormRefillDate(a?.refill_date || "");
    setFormStages(Object.fromEntries(
      [...new Set([...DEFAULT_CASE_STAGES.map((s) => s.key), ...caseStages.map((s) => s.key)])]
        .map((k) => [k, !!a?.[k]])
    ));
    setFormStaffTouched(false);
    setFormReplacementMonths(a?.replacement_months || 0);
    setFormReason(a?.reason || "");
    // Tri-state health card status. Prefer the explicit string; fall back to
    // the legacy boolean for pre-migration records.
    setFormHealthCard(
      (a?.health_card_status || (a?.has_health_card ? "yes" : "no"))
    );
    setFormDOB(a?.dob || "");
    setFormHCN(a?.hcn || "");
    // Registration date: use stored value for existing appts; auto-set to
    // today for new bookings.
    setFormRegDate(a?.registration_date || toYMD(new Date()));
    // Recovered date: only meaningful when health_card_status === 'recovered'.
    setFormRecoveredDate(a?.recovered_date || "");
    // Reschedule defaults: existing date+slot for edit; today+slot for new
    setFormApptDate(a?.date || (slot.__hold ? "" : slot.__new ? toYMD(new Date()) : selectedYMD));
    setFormApptSlot(a?.slot_time || (slot.__hold ? "" : slot.__new ? (slot.__slot || "") : slot.slot_time));
    setRescheduleSlots([]);
  };

  const closeSlot = () => {
    setSlotDialog(null);
    setFormName("");
    setFormNotes("");
    setFormClientType("new");
    setFormReferralType("outreach");
    setFormStatus("scheduled");
    setFormScheduledBy("");
    setFormReferralDone("review");
    setFormHealthCard("no");
    setFormDOB("");
    setFormHCN("");
    setFormRegDate("");
    setFormRecoveredDate("");
    setFormMedication("");
    setFormRefill(false);
    setFormRefillDate("");
    setFormStages({});
    setFormStaffTouched(false);
    setFormReplacementMonths(0);
    setFormReason("");
    setFormApptDate("");
    setFormApptSlot("");
    setRescheduleSlots([]);
    // If this slot dialog was opened from the stats drill-down, restore the
    // drill-down modal so the user lands back where they were — refresh the
    // list to reflect any edits (status change, reschedule, delete).
    if (statsDetailReturn) {
      const ret = statsDetailReturn;
      setStatsDetailReturn(null);
      // Re-fetch the list so any edits are reflected.
      (async () => {
        try {
          setStatsDetail({ ...ret, loading: true });
          let first, last;
          if (statsRange) {
            first = statsRange.from; last = statsRange.to;
          } else {
            const [y, m] = statsMonth.split("-").map(Number);
            first = `${y}-${String(m).padStart(2, "0")}-01`;
            const lastDay = new Date(y, m, 0).getDate();
            last  = `${y}-${String(m).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
          }
          const { _reg_scope, _exclude_holds_and_replacements, ...cleanParams } = ret.params || {};
          const reqParams = _reg_scope
            ? { ...cleanParams }
            : { from: first, to: last, ...cleanParams };
          const { data } = await api.get("/appointments", {
            params: reqParams,
          });
          let items = data || [];
          if (_exclude_holds_and_replacements) {
            items = items.filter((a) =>
              (a.status || "") !== "hold" && (a.client_type || "") !== "replacement"
            );
          }
          setStatsDetail({ ...ret, items, loading: false });
          // Also refresh top-level stats since counts may have shifted.
          loadStats();
        } catch {
          setStatsDetail(ret);
        }
      })();
    }
  };

  // Fetch that day's available slots when the user picks (or the record
  // already has) an appointment date. Runs for both:
  //   • edit mode — populates the reschedule picker,
  //   • new-hold mode — lets the user optionally add a slot to a hold.
  useEffect(() => {
    if (!slotDialog || !formApptDate) return;
    const isHold = formStatus === "hold" || formStatus === "not_qualified";
    // Skip for calendar-slot bookings (they already picked a slot) — but the
    // __new quick-add and hold flows need this day's slots for their picker.
    if (!slotDialog.appointment && !isHold && !slotDialog.__new) return;
    let cancelled = false;
    (async () => {
      setRescheduleLoading(true);
      try {
        const { data } = await api.get(`/day/${formApptDate}`);
        if (cancelled) return;
        const all = (data?.slots || []).map((s) => ({
          slot_time: s.slot_time,
          taken: !!s.appointment && s.appointment.id !== slotDialog.appointment?.id,
        }));
        setRescheduleSlots(all);
        // Snap current slot to something valid if the picked date has no slots
        if (!all.some((s) => s.slot_time === formApptSlot) && all.length) {
          const open = all.filter((s) => !s.taken);
          // __new: snap to the nearest open slot at/after now — and ONLY
          // that. If nothing is left today, leave it blank ("Pick a time")
          // instead of jumping back to a morning slot.
          if (slotDialog.__new && slotDialog.__now) {
            const afterNow = open.find((s) => s.slot_time >= slotDialog.__now);
            setFormApptSlot(afterNow ? afterNow.slot_time : "");
          } else {
            const firstOpen = open[0];
            setFormApptSlot(firstOpen ? firstOpen.slot_time : "");
          }
        }
      } catch {
        if (!cancelled) setRescheduleSlots([]);
      } finally {
        if (!cancelled) setRescheduleLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formApptDate, slotDialog?.appointment?.id, slotDialog?.__hold, slotDialog?.__new, formStatus]);

  const saveSlot = async (e) => {
    e?.preventDefault?.();
    const name = formName.trim();
    if (!name) {
      toast.error("Patient name is required.");
      return;
    }
    if (!formScheduledBy) {
      toast.error("Scheduled by is required — pick which staff member is booking.");
      return;
    }
    if (!formMedication && medStageNeedsDrug) {
      toast.error("Medication is required from the Prescription stage onward — pick from the dropdown.");
      return;
    }
    if (formClientType === "replacement" && (formReplacementMonths || 0) < 1) {
      toast.error("Replacement clients require a duration (1, 2 or 3 months).");
      return;
    }
    if (formRefill && !formRefillDate) {
      toast.error("Pick the next refill date — Refills is set to Yes.");
      return;
    }
    const isHold = formStatus === "hold" || formStatus === "not_qualified";
    const isReplacement = formClientType === "replacement";
    // Switching a hold back to an active status requires a real slot —
    // never save a scheduled record without a date/time.
    const noSlotStatus = isHold || formStatus === "not_required";
    if (!noSlotStatus && !isReplacement && (!formApptDate || !formApptSlot)) {
      toast.error("Pick an appointment date and time to schedule this client.");
      return;
    }
    // Replacement clients don't consume a doctor's schedule slot at all.
    // Hold + Not-qualified MAY carry an appointment slot now — if the user
    // filled both date + slot we send them and the backend validates.
    const holdWithSlot = noSlotStatus && !!formApptDate && !!formApptSlot;
    const noSlot = (noSlotStatus && !holdWithSlot) || isReplacement;
    setSavingSlot(true);
    try {
      if (slotDialog.appointment) {
        await api.patch(`/appointments/${slotDialog.appointment.id}`, {
          patient_name: name,
          notes: formNotes,
          date: noSlot ? "" : formApptDate,
          slot_time: noSlot ? "" : formApptSlot,
          client_type: formClientType,
          status: formStatus,
          scheduled_by: staffScope || formScheduledBy,
          ...(formStaffTouched ? { staff_override: true } : {}),
          referral_completed: formReferralDone,
          referral_type: formReferralType,
          has_health_card: formHealthCard === "yes",
          health_card_status: formHealthCard,
          dob: formDOB,
          hcn: formHCN.trim(),
          registration_date: formRegDate,
          recovered_date: formHealthCard === "recovered" ? formRecoveredDate : "",
          medication: formMedication,
          refill: formRefill,
          refill_date: formRefill ? formRefillDate : "",
          ...Object.fromEntries(caseStages.map((s) => [s.key, !!formStages[s.key]])),
          replacement_months: formClientType === "replacement" ? (formReplacementMonths || 0) : 0,
          reason: isHold ? (formReason || "") : "",
        });
        toast.success(
          formStatus === "not_qualified" ? "Not qualified saved."
          : isHold ? "Hold saved."
          : (isReplacement ? "Replacement client saved." : "Appointment updated.")
        );
      } else {
        await api.post("/appointments", {
          date: noSlot ? "" : ((holdWithSlot || slotDialog.__new) ? formApptDate : selectedYMD),
          slot_time: noSlot ? "" : ((holdWithSlot || slotDialog.__new) ? formApptSlot : slotDialog.slot_time),
          patient_name: name,
          notes: formNotes,
          client_type: formClientType,
          status: formStatus,
          scheduled_by: staffScope || formScheduledBy,
          staff_override: true,
          referral_completed: formReferralDone,
          referral_type: formReferralType,
          has_health_card: formHealthCard === "yes",
          health_card_status: formHealthCard,
          dob: formDOB,
          hcn: formHCN.trim(),
          registration_date: formRegDate,
          recovered_date: formHealthCard === "recovered" ? formRecoveredDate : "",
          medication: formMedication,
          refill: formRefill,
          refill_date: formRefill ? formRefillDate : "",
          ...Object.fromEntries(caseStages.map((s) => [s.key, !!formStages[s.key]])),
          replacement_months: formClientType === "replacement" ? (formReplacementMonths || 0) : 0,
          reason: isHold ? (formReason || "") : "",
        });
        toast.success(
          formStatus === "not_qualified" ? "Not qualified created."
          : isHold ? "Hold created."
          : (isReplacement ? "Replacement client created." : "Appointment booked.")
        );
      }
      // If we rescheduled to a different date, jump the calendar to that date
      // so the user visually sees the moved appointment. Same for the New
      // quick-add flow: it always books on the form's date (today), which
      // may differ from the day currently on screen.
      const movedDate = slotDialog.appointment && formApptDate && formApptDate !== slotDialog.appointment.date
        ? formApptDate
        : null;
      const newFlowDate = !slotDialog.appointment && slotDialog.__new && formApptDate && formApptDate !== selectedYMD
        ? formApptDate
        : null;
      const jumpDate = movedDate || newFlowDate;
      closeSlot();
      if (jumpDate) {
        setSelectedDate(fromYMD(jumpDate));
        await loadDay(jumpDate);
      } else {
        await loadDay(selectedYMD);
      }
      loadAvailability();
      if (tab === "history") loadHistory();
      if (tab === "stats") loadStats();
      // If the user opened this record from the global search, refetch
      // that dropdown too so the cached row shows the latest dob/hcn/etc.
      if (globalQ && globalQ.trim()) {
        try {
          const { data } = await api.get("/appointments", { params: { q: globalQ.trim() } });
          setGlobalResults((data || []).slice(0, 12).map((a) => ({
            ...a, __kind: "appointment",
          })));
        } catch { /* ignore */ }
      }
      // If the edit came from the Invoicing panel, bump refreshKey so the
      // invoice tables re-fetch. Otherwise the row that opened the dialog
      // stays stale (missing the DOB/HCN the user just entered) and the
      // next open looks like nothing was saved.
      if (slotDialog?.__from_invoicing) {
        setRefreshKey((k) => k + 1);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save appointment.");
    } finally {
      setSavingSlot(false);
    }
  };

  const deleteAppt = async (appt) => {
    try {
      await api.delete(`/appointments/${appt.id}`);
      toast.success("Appointment deleted.");
      setConfirmDelete(null);
      closeSlot();
      await loadDay(selectedYMD);
      if (tab === "history") loadHistory();
      if (tab === "stats") loadStats();
    } catch {
      toast.error("Failed to delete appointment.");
    }
  };

  // ---------- calendar dots ----------
  const daysWithAvail = useMemo(
    () => Object.keys(availabilityMap).map(fromYMD),
    [availabilityMap],
  );
  const modifiers = { hasAvail: daysWithAvail };
  const modifiersClassNames = {
    // `ring-inset` keeps the availability ring inside the day button so
    // adjacent day rings can never touch/overlap on narrow (mobile) cells.
    // When the day is the SELECTED one, go solid dark-green (same as a
    // selected plain day) so picking a date always looks identical.
    hasAvail:
      "!bg-[#0F3B2E]/12 !text-[#0F3B2E] !font-semibold !ring-1 !ring-inset !ring-[#0F3B2E]/40 aria-selected:!bg-[#0F3B2E] aria-selected:!text-white",
  };

  // Same selected-day look as the Calendar tab: the picked date is a solid
  // dark-green fill (never plain white); availability-tinted days go solid
  // when selected via the modifier above.
  const calendarClassNames = {
    day_selected: "!bg-[#0F3B2E] !text-white !font-semibold ring-2 ring-inset ring-[#0F172A]/40",
    day_today: "ring-1 ring-inset ring-[#0F172A]/25",
    day_outside: "day-outside",
  };

  const bookedCount = day?.appointments?.length || 0;
  const totalSlots = day?.slots?.length || 0;
  const pendingReferrals = (day?.appointments || []).filter((a) => {
    const rc = a.referral_completed;
    // Tri-state or legacy bool. "not_completed" / false / missing counts as pending.
    if (rc === "completed" || rc === true) return false;
    if (rc === "not_required") return false;
    return true;
  }).length;

  // Group slots by window for visual separation (so a gap between windows is
  // rendered as a labelled section rather than as one long ambiguous grid).
  // Days with no availability set fall back to the backend's default-hours
  // grid — one unlabeled group holding every slot.
  const slotGroups = useMemo(() => {
    if (!day?.slots?.length) return [];
    const windows = day?.availability?.windows || [];
    if (!windows.length) {
      return [{ label: "", startMin: 0, endMin: 24 * 60, slots: day.slots }];
    }
    const groups = windows.map((w) => ({
      label: `${prettyTime(w.start_time)} – ${prettyTime(w.end_time)}`,
      startMin: toMins(w.start_time),
      endMin: toMins(w.end_time),
      slots: [],
    }));
    for (const s of day.slots) {
      const m = toMins(s.slot_time);
      const grp = groups.find((g) => m >= g.startMin && m < g.endMin);
      if (grp) grp.slots.push(s);
    }
    return groups.filter((g) => g.slots.length);
  }, [day]);

  // ============================ render ============================
  return (
    <div
      data-testid="consult-dashboard"
      style={{
        minHeight: "100vh",
        background: BG,
        color: TEXT,
        fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
      }}
    >
      {/* Spinning keyframes for the header Refresh button icon.
          Also collapse button text labels on narrow screens so the
          Refresh + Sign out pair stops colliding with the title. */}
      <style>{`
        @keyframes hepcure-spin { to { transform: rotate(360deg); } }
        @media (max-width: 480px) {
          .consult-hdr-label { display: none; }
        }
        @media (max-width: 720px) {
          .staff-preview-chip { display: none; }  /* note bar below the header carries it */
        }
      `}</style>      {/* Header — matches content column width so it aligns with cards below */}
      <div style={{ padding: "20px 20px 0 20px", maxWidth: 1280, margin: "0 auto" }}>
      <header
        style={{
          background: "#FFFFFF",
          border: `1px solid ${BORDER}`,
          borderRadius: 14,
          padding: "14px 16px",
        }}
      >
        {/* Row 1: title + sign out */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, marginBottom: 12,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, background: DARK_GREEN,
              color: "white", display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <CalendarDays style={{ width: 20, height: 20 }} strokeWidth={2} />
            </div>
            <div style={{ minWidth: 0 }}>
              <h1 style={{
                fontFamily: "'Work Sans', system-ui, sans-serif",
                fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: "-0.01em",
              }}>Dashboard</h1>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            {/* New patient button — visible on EVERY tab (even when the MD
                tab itself is hidden), but only for admins and users granted
                the "Outreach admin" function on their profile; plain staff
                (e.g. expenses-only accounts) never see it. */}
            {(perms.isAdmin || perms.outreach_edit) && (
            <button
                data-testid="consult-new-btn"
                onClick={async () => {
                  // New patient — date defaults to today, time to the nearest
                  // open slot at/after now (resolved up front so the dialog
                  // opens fully defaulted; Clear date & time resets it).
                  const now = new Date();
                  const nowHM = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
                  const today = toYMD(now);
                  let slot = "";
                  try {
                    const { data } = await api.get(`/day/${today}`);
                    const open = (data?.slots || []).filter((s) => !s.appointment);
                    // Next open slot at/after RIGHT NOW only — never fall
                    // back to an earlier (past) slot; if none is left today
                    // the picker stays blank for a manual choice.
                    const after = open.find((s) => s.slot_time >= nowHM);
                    slot = (after || {}).slot_time || "";
                  } catch { /* leave empty — user picks or clears */ }
                  openSlot({ __new: true, slot_time: "", __now: nowHM, __slot: slot });
                }}
                title="Add a new patient (date/time default to right now)"
                style={{
                  display: "flex", alignItems: "center", gap: 6,
                  background: "#E58A2E", color: "#fff", border: 0,
                  borderRadius: 8, padding: "6px 12px",
                  cursor: "pointer",
                  fontSize: 12, fontWeight: 700,
                  boxShadow: "0 1px 2px rgba(229,138,46,0.35)",
                }}
              >
                <UserPlus style={{ width: 13, height: 13 }} strokeWidth={2} />
                <span className="consult-hdr-label">New</span>
              </button>
            )}
            {staffScope && (
              <span data-testid="staff-preview-chip" className="staff-preview-chip" style={{
                fontSize: 11, fontWeight: 700, color: DARK_GREEN,
                border: "1px solid rgba(15,59,46,.4)", background: "rgba(15,59,46,.06)",
                borderRadius: 999, padding: "6px 12px", whiteSpace: "nowrap",
              }}>{staffScope} · {previewStaff ? "Staff preview" : "Staff"}</span>
            )}
            <button
              data-testid="consult-refresh-btn"
              onClick={doRefresh}
              disabled={refreshing}
              title="Reload appointments, outreach, stats & invoicing without signing out"
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: refreshing ? BORDER : "transparent",
                border: `1px solid ${BORDER}`,
                borderRadius: 8, padding: "6px 10px",
                cursor: refreshing ? "default" : "pointer",
                fontSize: 12, fontWeight: 600, color: MUTED,
                opacity: refreshing ? 0.7 : 1,
              }}
            >
              <RefreshCw
                style={{
                  width: 13, height: 13,
                  animation: refreshing ? "hepcure-spin 0.9s linear infinite" : "none",
                }}
                strokeWidth={2}
              />
              <span className="consult-hdr-label">{refreshing ? "Refreshing…" : "Refresh"}</span>
            </button>
            <button
              data-testid="consult-logout"
              onClick={doLogout}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                background: "transparent", border: `1px solid ${BORDER}`,
                borderRadius: 8, padding: "6px 10px", cursor: "pointer",
                fontSize: 12, fontWeight: 600, color: MUTED,
              }}
            >
              <LogOut style={{ width: 13, height: 13 }} strokeWidth={2} />
              <span className="consult-hdr-label">Sign out</span>
            </button>
          </div>
        </div>

        {/* Row 2: search — only for accounts with client-facing sections */}
        {showGlobalSearch && (
        <div ref={globalRef} style={{ position: "relative", marginBottom: 10 }}>
          <Search style={{
            position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            width: 15, height: 15, color: MUTED, pointerEvents: "none",
          }} />
          <input
            ref={globalInputRef}
            type="text"
            value={globalQ}
            onChange={(e) => setGlobalQ(e.target.value)}
            onFocus={() => globalResults.length && setGlobalOpen(true)}
            placeholder="Search by name…"
            data-testid="consult-global-search"
            style={{
              width: "100%", height: 40, borderRadius: 10, paddingLeft: 36, paddingRight: 12,
              fontSize: 14, background: "#F9F8F6", border: `1px solid ${BORDER}`,
              outline: "none", color: TEXT, boxSizing: "border-box",
              fontFamily: "inherit",
            }}
          />
          {globalOpen && globalResults.length > 0 && (
            <div
              data-testid="consult-global-results"
              style={{
                position: "absolute", top: 46, left: 0, right: 0,
                background: "white", borderRadius: 10, border: `1px solid ${BORDER}`,
                boxShadow: "0 10px 24px rgba(15,59,46,0.14)",
                overflow: "hidden", zIndex: 30, maxHeight: 320, overflowY: "auto",
              }}
            >
              {globalResults.map((item) => {
                if (item.__kind === "incentive") {
                  const teamShort = /^team_\d+$/.test(item.team || "") ? `Team ${item.team.split("_")[1]}` : "Team 1";
                  const amt = `$${(Number(item.amount) || 0).toFixed(2)}`;
                  const typeLabel = item.type === "eft" ? "EFT" : "Cash";
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => jumpToIncentive(item)}
                      data-testid={`consult-global-result-${item.id}`}
                      style={{
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        width: "100%", padding: "10px 12px", border: "none", background: "white",
                        borderBottom: `1px solid ${BORDER}`, cursor: "pointer", textAlign: "left",
                        gap: 12,
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = "#F4F3EF"}
                      onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>
                          {`${item.first_name || ""} ${item.last_name || ""}`.trim() || "—"}
                        </div>
                        <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                          {prettyDateShort(item.date)} · {teamShort} · {typeLabel}
                        </div>
                      </div>
                      <div style={{
                        fontSize: 13, fontWeight: 700, color: COMPLETED_COL,
                        flexShrink: 0, fontVariantNumeric: "tabular-nums",
                      }}>
                        {amt}
                      </div>
                    </button>
                  );
                }
                // appointment shape (default)
                const appt = item;
                return (
                  <button
                    key={appt.id}
                    type="button"
                    onClick={() => jumpToAppt(appt)}
                    data-testid={`consult-global-result-${appt.id}`}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      width: "100%", padding: "10px 12px", border: "none", background: "white",
                      borderBottom: `1px solid ${BORDER}`, cursor: "pointer", textAlign: "left",
                      gap: 12,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "#F4F3EF"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: TEXT }}>
                        {appt.patient_name}
                      </div>
                      <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                        {prettyDateShort(appt.date)} · {prettyTime(appt.slot_time)}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      <StatusPill status={appt.status || "scheduled"} />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        )}

        {/* Row 3: tabs (always one line) */}
        <div className="consult-tabs" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
          {tabAllowed("day") && (
            <button data-testid="consult-tab-day" onClick={() => setTab("day")} style={tabBtn(tab === "day")}>
              <CalendarIcon style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span className="hidden sm:inline">MD schedule</span>
                <span className="sm:hidden">MD</span>
              </span>
            </button>
          )}
          {!staffScope && tabAllowed("history") && (
            <button data-testid="consult-tab-history" onClick={() => setTab("history")} style={tabBtn(tab === "history")}>
              <History style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span className="hidden sm:inline">All appointments</span>
                <span className="sm:hidden">All appts</span>
              </span>
            </button>
          )}
          {tabAllowed("pharmacy") && (
            <button data-testid="consult-tab-pharmacy" onClick={() => setTab("pharmacy")} style={tabBtn(tab === "pharmacy")}>
              <Pill style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Pharmacy</span>
            </button>
          )}
          {tabAllowed("outreach") && (
            <button data-testid="consult-tab-outreach" onClick={() => setTab("outreach")} style={tabBtn(tab === "outreach")}>
              <CalendarIcon style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Calendar</span>
            </button>
          )}
          {tabAllowed("inventory") && (
            <button data-testid="consult-tab-inventory" onClick={() => setTab("inventory")} style={tabBtn(tab === "inventory")}>
              <Package style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Inventory</span>
            </button>
          )}
          {tabAllowed("incentives") && (
            <button data-testid="consult-tab-incentives" onClick={() => setTab("incentives")} style={tabBtn(tab === "incentives")}>
              <DollarSign style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Rewards</span>
            </button>
          )}
          {!staffScope && tabAllowed("referrals") && (
            <button data-testid="consult-tab-referrals" onClick={() => setTab("referrals")} style={tabBtn(tab === "referrals")}>
              <Share2 style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Referrals</span>
            </button>
          )}
          {tabAllowed("expenses") && (
            <button data-testid="consult-tab-expenses" onClick={() => setTab("expenses")} style={tabBtn(tab === "expenses")}>
              <Receipt style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Expenses</span>
            </button>
          )}
          {tabAllowed("stats") && (
            <button data-testid="consult-tab-stats" onClick={() => setTab("stats")} style={tabBtn(tab === "stats")}>
              <BarChart3 style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Stats</span>
            </button>
          )}
          {tabAllowed("locker") && (
            <button data-testid="consult-tab-locker" onClick={() => setTab("locker")} style={tabBtn(tab === "locker")}>
              <Boxes style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Locker</span>
            </button>
          )}
          {tabAllowed("marketing") && (
            <button data-testid="consult-tab-marketing" onClick={() => setTab("marketing")} style={tabBtn(tab === "marketing")}>
              <Megaphone style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Marketing</span>
            </button>
          )}
          {tabAllowed("bloodwork") && (
            <button data-testid="consult-tab-bloodwork" onClick={() => setTab("bloodwork")} style={tabBtn(tab === "bloodwork")}>
              <Droplets style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Bloodwork</span>
            </button>
          )}
          {tabAllowed("survey") && (
            <button data-testid="consult-tab-survey" onClick={() => setTab("survey")} style={tabBtn(tab === "survey")}>
              <ClipboardList style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Survey</span>
            </button>
          )}
          {tabAllowed("invoicing") && !previewStaff && (
            <button data-testid="consult-tab-invoicing" onClick={() => setTab("invoicing")} style={tabBtn(tab === "invoicing")}>
              <Receipt style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <span className="hidden sm:inline">Invoicing</span>
                <span className="sm:hidden">Invoice</span>
              </span>
            </button>
          )}
        </div>
      </header>
      </div>

      {previewStaff && (
        <div style={{ maxWidth: 1280, margin: "14px auto 0", padding: "0 20px" }} data-testid="staff-preview-note">
          <div style={{ background: "rgba(31,78,121,.06)", border: "1px solid rgba(31,78,121,.3)", borderRadius: 10, padding: "9px 14px", fontSize: 12, color: "#1F4E79" }}>
            <b>Staff preview — {previewStaff}.</b> The outreach calendar, expenses and stats are scoped to this staff member, Referrals + Invoicing are hidden, and the All-appointments list is admin-only. The MD schedule stays shared with the whole team. This preview filters in the browser for review; staff logins are enforced on the server.
          </div>
        </div>
      )}

      <main style={{ maxWidth: 1280, margin: "0 auto", padding: "20px" }}>
        {tab === "day" && (
          <div
            className="consult-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(320px, 440px) 1fr",
              gap: 24,
              alignItems: "start",
            }}
          >
            {/* Left column */}
            <div className="outreach-card" style={{
              background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
              padding: 20,
            }}>
              {/* Staff member strip — mirrors the Calendar tab so both tabs
                  line up when flipping between them. Staff logins are locked
                  to their own pill; for admin the picked pill becomes the
                  default "Scheduled by" on new bookings. */}
              <div style={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                marginBottom: staffScope ? 20 : 12, gap: 8, minHeight: 32, flexWrap: "nowrap",
              }}>
                <p style={{ ...labelStyle, margin: 0 }}>Staff member</p>
                {/* Staff logins: their name sits on this same line (same as
                    the Calendar tab) — no separate pill row below. */}
                {staffScope && (
                  <span
                    data-testid={`consult-day-staff-${staffScope.split(" ")[0].toLowerCase()}`}
                    style={{
                      padding: "8px 14px", borderRadius: 8,
                      border: "1.5px solid #2E7D5B", background: "#2E7D5B",
                      color: "white", fontSize: 11, fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {staffScope.split(" ")[0]}
                  </span>
                )}
              </div>
              {/* Admin only: the full staff strip. Staff logins don't get this
                  row (their pill is on the label line above). */}
              {!staffScope && (
              <div
                className="hide-scrollbar"
                data-testid="consult-day-staff-strip"
                style={{
                  display: "flex", gap: 6, marginBottom: 20,
                  overflowX: "auto", flexWrap: "nowrap",
                  WebkitOverflowScrolling: "touch",
                  paddingBottom: 2,
                }}
              >
                {staffNamesForMonth(selectedYMD.slice(0, 7)).map((full) => {
                  const short = full.split(" ")[0];
                  const active = full === mdStaff;
                  return (
                    <button
                      key={full}
                      type="button"
                      onClick={() => setMdStaff(full)}
                      data-testid={`consult-day-staff-${short.toLowerCase()}`}
                      style={{
                        padding: "8px 14px",
                        borderRadius: 8,
                        border: `1.5px solid ${active ? "#2E7D5B" : BORDER}`,
                        background: active ? "#2E7D5B" : "white",
                        color: active ? "white" : MUTED,
                        fontSize: 11, fontWeight: 600,
                        cursor: "pointer",
                        fontFamily: "inherit",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {short}
                    </button>
                  );
                })}
              </div>
              )}
              <p style={{ ...labelStyle, marginBottom: 8, textAlign: "center" }}>Pick a date</p>
              <div style={{ display: "flex", justifyContent: "center" }}>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  modifiers={modifiers}
                  modifiersClassNames={modifiersClassNames}
                  classNames={calendarClassNames}
                  className="p-0"
                  data-testid="consult-calendar"
                />
              </div>
              <div style={{
                marginTop: 14, display: "flex", flexDirection: "column",
                alignItems: "center", gap: 10,
              }}>
                <button
                  type="button"
                  data-testid="consult-today-btn"
                  onClick={() => setSelectedDate(new Date())}
                  style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    fontSize: 12, fontWeight: 600, color: DARK_GREEN,
                    background: "rgba(15,59,46,0.06)",
                    border: "1px solid rgba(15,59,46,0.20)",
                    borderRadius: 999, padding: "6px 14px", cursor: "pointer",
                  }}
                >
                  Jump to today
                </button>
              </div>

              {/* Availability: read-only chips + instant add/remove. No
                  whole-day save — each add/remove is its own server call, so
                  a day can never be wiped by a stale form and a window with
                  booked patients can't be removed. */}
              <div style={{ marginTop: 20, borderTop: `1px solid ${BORDER}`, paddingTop: 16 }}>
                <p style={{ ...labelStyle, marginBottom: 8 }}>Doctor availability</p>
                <p style={{ fontSize: 13, color: MUTED, margin: "0 0 12px", lineHeight: 1.5 }}>
                  {format(selectedDate, "EEEE, MMM d, yyyy")}
                </p>
                {(!day || day.date !== selectedYMD) ? (
                  <div
                    data-testid="consult-avail-loading"
                    style={{
                      fontSize: 13, color: MUTED, padding: "10px 12px",
                      background: "#F9F8F6", borderRadius: 10, border: `1px solid ${BORDER}`,
                    }}
                  >
                    Loading this day's schedule…
                  </div>
                ) : (
                  <>
                    {currentWindows.length === 0 ? (
                      <p style={{ fontSize: 13, color: MUTED, margin: "0 0 10px" }}>
                        No availability set for this day yet — add a window below.
                      </p>
                    ) : (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                        {currentWindows.map((w, idx) => {
                          const booked = bookedCountInWindow(w);
                          return (
                            <span
                              key={`${w.start_time}-${w.end_time}`}
                              data-testid={`consult-window-chip-${idx}`}
                              style={{
                                display: "inline-flex", alignItems: "center", gap: 8,
                                background: "#fff", border: `1px solid ${BORDER}`,
                                borderRadius: 999, padding: "7px 8px 7px 13px",
                                fontSize: 13, fontWeight: 600, color: TEXT,
                              }}
                            >
                              {prettyTime(w.start_time)} – {prettyTime(w.end_time)}
                              {booked > 0 && (
                                <span style={{
                                  fontSize: 10, fontWeight: 700, color: "#1F7A70",
                                  background: "rgba(42,159,146,.12)", borderRadius: 999,
                                  padding: "2px 7px",
                                }}>
                                  {booked} booked
                                </span>
                              )}
                              <button
                                type="button"
                                data-testid={`consult-window-remove-${idx}`}
                                onClick={() => removeAvailabilityWindow(w)}
                                disabled={savingAvail}
                                aria-label={`Remove window ${prettyTime(w.start_time)} to ${prettyTime(w.end_time)}`}
                                style={{
                                  background: "transparent", border: "none", cursor: "pointer",
                                  color: MISSED_COL, display: "flex", alignItems: "center",
                                  padding: 2, opacity: savingAvail ? 0.5 : 1,
                                }}
                              >
                                <XIcon style={{ width: 13, height: 13 }} strokeWidth={2.5} />
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}

                    <div style={{
                      display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                      background: "#F9F8F6", border: `1px solid ${BORDER}`,
                      borderRadius: 10, padding: "10px 12px",
                    }}>
                      <TimePicker15
                        value={newWindow.start_time}
                        onChange={(v) => setNewWindow((p) => ({ ...p, start_time: v }))}
                        testIdPrefix="consult-window-new-start"
                      />
                      <span style={{ color: MUTED, fontWeight: 500, fontSize: 13 }}>to</span>
                      <TimePicker15
                        value={newWindow.end_time}
                        onChange={(v) => setNewWindow((p) => ({ ...p, end_time: v }))}
                        testIdPrefix="consult-window-new-end"
                      />
                      <button
                        type="button"
                        data-testid="consult-window-add"
                        onClick={addAvailabilityWindow}
                        disabled={savingAvail}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 6,
                          background: DARK_GREEN, color: "white", border: "none",
                          borderRadius: 999, padding: "9px 16px", fontSize: 13, fontWeight: 600,
                          cursor: savingAvail ? "default" : "pointer", opacity: savingAvail ? 0.6 : 1,
                          marginLeft: "auto",
                        }}
                      >
                        <Plus style={{ width: 14, height: 14 }} strokeWidth={2.5} />
                        Add window
                      </button>
                    </div>

                    {currentWindows.length > 0 && (
                      <div style={{ marginTop: 10, textAlign: "right" }}>
                        <button
                          type="button"
                          onClick={clearAvailability}
                          data-testid="consult-avail-clear"
                          style={{
                            background: "white", color: MISSED_COL,
                            border: "1px solid #E9BFB1", borderRadius: 8,
                            padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer",
                          }}
                        >
                          Clear day
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Right column: slots grouped by window */}
            <div style={{
              background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
              padding: 20, minHeight: 400,
            }}>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                marginBottom: 16, flexWrap: "wrap", gap: 12,
              }}>
                <div>
                  <h2 style={{
                    fontFamily: "'Work Sans', system-ui, sans-serif",
                    fontSize: 20, fontWeight: 700, margin: 0, color: TEXT,
                  }}>
                    {prettyDate(selectedYMD)}
                  </h2>
                  {totalSlots > 0 && (
                    <p style={{ fontSize: 13, color: MUTED, margin: "4px 0 0" }}>
                      {bookedCount} of {totalSlots} slots booked
                      {currentWindows.length ? (
                        <>
                          {" · "}
                          {currentWindows.map((w, i) => (
                            <span key={i}>
                              {i > 0 ? " · " : ""}
                              {prettyTime(w.start_time)}–{prettyTime(w.end_time)}
                            </span>
                          ))}
                        </>
                      ) : null}
                    </p>
                  )}
                </div>
                {pendingReferrals > 0 && (
                  <span
                    data-testid="consult-pending-referrals"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(180,53,27,0.10)",
                      color: REF_PENDING_COL,
                      border: `1px solid ${REF_PENDING_COL}55`,
                      borderRadius: 999, padding: "5px 12px",
                      fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                    }}
                  >
                    <AlertCircle style={{ width: 13, height: 13 }} strokeWidth={2.5} />
                    {pendingReferrals} referral{pendingReferrals === 1 ? "" : "s"} pending
                  </span>
                )}
              </div>

              {/* While switching days, show the placeholder. When RELOADING
                  the same day (e.g. after booking a slot), keep the current
                  slots on screen so the page never collapses and jumps the
                  user's scroll position. */}
              {loadingDay && day?.date !== selectedYMD ? (
                <div style={{ color: MUTED, padding: 40, textAlign: "center" }}>Loading…</div>
              ) : totalSlots === 0 ? (
                <EmptyState
                  icon={<Clock style={{ width: 32, height: 32, color: MUTED }} />}
                  title="No bookable slots for this day"
                  body="Use the panel on the left to define one or more time windows for the doctor. Once you save, 15-minute slots will appear here."
                />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {!currentWindows.length && (
                    <div
                      data-testid="consult-default-hours-note"
                      style={{
                        fontSize: 12, color: "#7A4400", fontWeight: 500,
                        background: "rgba(229,138,46,0.10)", border: "1px solid rgba(229,138,46,0.35)",
                        borderRadius: 8, padding: "8px 12px",
                      }}
                    >
                      No availability set for this day — showing default hours (12:00 PM – 8:00 PM). You can book normally, or set the doctor's hours on the left.
                    </div>
                  )}
                  {slotGroups.map((grp, gi) => (
                    <div key={gi}>
                      {slotGroups.length > 1 && (
                        <p style={{
                          fontSize: 11, fontWeight: 700, color: LABEL_GREEN,
                          letterSpacing: "0.18em", textTransform: "uppercase",
                          margin: "0 0 10px",
                        }}>
                          {grp.label}
                        </p>
                      )}
                      <div style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                        gap: 10,
                      }}>
                        {grp.slots.map((slot) => (
                          <SlotCard
                            key={slot.slot_time}
                            slot={slot}
                            onClick={() => openSlot(slot)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "history" && (
          <HistoryPanel
            items={historyItems}
            loading={historyLoading}
            q={historyQ}
            setQ={setHistoryQ}
            mode={historyMode}
            setMode={setHistoryMode}
            statusFilter={historyStatus}
            setStatusFilter={setHistoryStatus}
            referralFilter={historyReferral}
            setReferralFilter={setHistoryReferral}
            referralSourceFilter={historyReferralSource}
            setReferralSourceFilter={setHistoryReferralSource}
            showHolds={historyShowHolds}
            setShowHolds={setHistoryShowHolds}
            onReload={loadHistory}
            onEdit={(appt) => {
              // Hold + not-qualified rows have no date/slot; open via
              // __hold path so the slot dialog shows the "Not scheduled
              // yet" placeholders and allows re-dispositioning.
              if (appt.status === "hold" || appt.status === "not_qualified" || !appt.date) {
                setTab("day");
                setTimeout(() => openSlot({ __hold: true, slot_time: "", appointment: appt }), 220);
              } else {
                setSelectedDate(fromYMD(appt.date));
                setTab("day");
                setTimeout(() => openSlot({ slot_time: appt.slot_time, appointment: appt }), 220);
              }
            }}
            onDelete={(appt) => setConfirmDelete(appt)}
          />
        )}

        {tab === "pharmacy" && (
          <PharmacyPanel
            refreshKey={refreshKey}
            onEdit={(appt) => {
              if (appt.status === "hold" || appt.status === "not_qualified" || !appt.date) {
                setTab("day");
                setTimeout(() => openSlot({ __hold: true, slot_time: "", appointment: appt }), 220);
              } else {
                setSelectedDate(fromYMD(appt.date));
                setTab("day");
                setTimeout(() => openSlot({ slot_time: appt.slot_time, appointment: appt }), 220);
              }
            }}
          />
        )}

        {tab === "stats" && (
          <StatsPanel
            month={statsMonth}
            setMonth={setStatsMonth}
            range={statsRange}
            setRange={setStatsRange}
            stats={stats}
            loading={statsLoading}
            onSegmentClick={openStatsDetail}
            medications={medications}
            onGoToOutreach={() => setTab("outreach")}
            staffFirst={staffScopeFirst}
            staffShorts={staffShorts}
            caseStages={caseStages}
          />
        )}
        {tab === "bloodwork" && (
          <BloodworkPanel isAdmin={!!perms.isAdmin} />
        )}
        {tab === "locker" && (
          <LockerPanel />
        )}
        {tab === "survey" && (
          <SurveyPanel />
        )}
        {tab === "marketing" && (
          <MarketingPanel refreshKey={refreshKey} />
        )}
        {tab === "outreach" && (
          <OutreachPanel canEdit={perms.outreach_edit} refreshKey={refreshKey} lockStaff={staffScope || null} canManageCities={perms.isAdmin && !previewStaff} />
        )}
        {tab === "inventory" && (
          <InventoryPanel refreshKey={refreshKey} />
        )}
        {tab === "incentives" && (
          <IncentivesPanel refreshKey={refreshKey} openRequest={incentiveOpenReq} isAdmin={!!perms.isAdmin} lockTeam={staffScopeTeam} lockTeamLabel={staffScopeTeamLabel} />
        )}
        {tab === "referrals" && !staffScope && (
          <ReferralsPanel refreshKey={refreshKey} />
        )}
        {tab === "expenses" && (
          <ExpensesPanel refreshKey={refreshKey} lockTeam={staffScopeTeam} lockTeamLabel={staffScopeTeamLabel} />
        )}
        {tab === "invoicing" && perms.invoicing && !previewStaff && (
          <InvoicingPanel
            refreshKey={refreshKey}
            onOpenAppointment={async (appt) => {
              // Open the patient's slot dialog as a modal overlay on top
              // of the Invoicing tab. We deliberately do NOT switch tabs
              // so the user stays on Invoicing and closing the dialog
              // returns them straight back to the invoice list.
              // CRITICAL: the invoice row is a PARTIAL projection (no notes,
              // referral state, stage checkboxes, status…). Opening the dialog
              // with it would blank those fields, and saving would WIPE the
              // saved record. Always fetch the full appointment first — one
              // client record across the whole system.
              try {
                const { data } = await api.get(`/appointments/${appt.id}`);
                if (data?.date) setSelectedDate(fromYMD(data.date));
                openSlot({ slot_time: data.slot_time || "09:00", appointment: data, __from_invoicing: true });
              } catch {
                toast.error("Could not load the patient record — try again.");
              }
            }}
          />
        )}
        {tab === "invoicing" && !perms.invoicing && (
          <div style={{
            background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
            padding: 40, textAlign: "center", color: MUTED,
          }}>
            You don&rsquo;t have access to invoicing. Ask an admin to enable the
            &ldquo;Invoicing&rdquo; permission on your account.
          </div>
        )}
      </main>

      {/* Slot dialog */}
      <Dialog open={!!slotDialog} onOpenChange={(o) => !o && closeSlot()}>
        <DialogContent data-testid="consult-slot-dialog" className="max-w-md max-h-[92vh] overflow-y-auto [&>button]:hidden">
          <DialogHeader>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <DialogTitle style={{ minWidth: 0 }}>
                {slotDialog?.__hold
                  ? (slotDialog?.appointment ? "Edit hold client" : "Add hold client")
                  : slotDialog?.__new && !slotDialog?.appointment
                    ? "Add new patient"
                    : formClientType === "replacement"
                      ? (slotDialog?.appointment ? "Edit replacement client" : "Add replacement client")
                      : (slotDialog?.appointment ? "Edit appointment" : "Schedule a consultation")}
              </DialogTitle>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
              {/* Paste-patient — reads the device clipboard (expects the
                  same "RE: / PHN: / DOB:" shape that Copy exports) and
                  auto-fills the Name / HCN / DOB fields. */}
              <button
                type="button"
                data-testid="consult-paste-patient-btn"
                title="Auto-fill Name / HCN / DOB from clipboard"
                onClick={async () => {
                  try {
                    const text = await navigator.clipboard.readText();
                    if (!text || !text.trim()) {
                      toast.error("Clipboard is empty");
                      return;
                    }
                    const parsed = parsePatientPaste(text);
                    let filled = 0;
                    if (parsed.patient_name) { setFormName(parsed.patient_name); filled += 1; }
                    if (parsed.hcn)          { setFormHCN(formatHCN(parsed.hcn)); filled += 1; }
                    if (parsed.dob)          { setFormDOB(parsed.dob);          filled += 1; }
                    if (filled === 0) toast.error("Nothing recognisable in clipboard — expected RE: / PHN: / DOB: lines");
                    else toast.success(`Autofilled ${filled} field${filled === 1 ? "" : "s"} from clipboard`);
                  } catch (_) {
                    toast.error("Couldn't read clipboard — grant permission or paste manually");
                  }
                }}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  height: 30, padding: "0 10px",
                  background: "rgba(15,59,46,0.06)", color: DARK_GREEN,
                  border: `1px solid rgba(15,59,46,0.25)`,
                  borderRadius: 8, cursor: "pointer",
                  fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                  flexShrink: 0,
                }}
              >
                <ClipboardPaste style={{ width: 13, height: 13 }} strokeWidth={2} />
                Paste
              </button>
              <button
                type="button"
                aria-label="Close"
                data-testid="consult-slot-dialog-close"
                onClick={() => closeSlot()}
                style={{
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  width: 30, height: 30, borderRadius: 8, border: `1px solid ${BORDER}`,
                  background: "#fff", color: MUTED, cursor: "pointer", flexShrink: 0,
                }}
              >
                <XIcon style={{ width: 14, height: 14 }} strokeWidth={2} />
              </button>
              </div>
            </div>
            <DialogDescription>
              {slotDialog && (() => {
                const isHold = formStatus === "hold" || formStatus === "not_qualified";
                const hasSlot = isHold && !!formApptDate && !!formApptSlot;
                if (isHold && !hasSlot) {
                  return (
                    <span style={{
                      color: formStatus === "not_qualified" ? "#B4351B" : "#E58A2E",
                      fontWeight: 700,
                    }}>
                      {formStatus === "not_qualified" ? "Not qualified · no date/time" : "Hold record · no date/time yet"}
                    </span>
                  );
                }
                if (isHold && hasSlot) {
                  return (
                    <span>
                      <span style={{
                        color: formStatus === "not_qualified" ? "#B4351B" : "#E58A2E",
                        fontWeight: 700,
                        marginRight: 6,
                      }}>
                        {formStatus === "not_qualified" ? "Not qualified" : "Hold"} ·
                      </span>
                      {prettyDateShort(formApptDate)} · <b>{prettyTime(formApptSlot)}</b>
                    </span>
                  );
                }
                if (formClientType === "replacement") {
                  return (
                    <span style={{ color: "#4A6FB0", fontWeight: 700 }}>
                      Replacement client · no doctor slot needed
                    </span>
                  );
                }
                {/* New patient: no subtitle — the title already says "Add
                    new patient" and the date/time box below already shows
                    (and edits) the when. It was a duplicate. */}
                if (slotDialog?.__new && !slotDialog?.appointment) {
                  return null;
                }
                if (slotDialog?.appointment) {
                return (
                  <>
                    {prettyDate(slotDialog.appointment?.date || selectedYMD)}
                    {" · "}
                    <b>{prettyTime(slotDialog.slot_time)}</b>
                  </>
                );
                }
                return null;
              })()}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveSlot} className="space-y-4">
            {/* Appointment date/time picker — shown for real appointments AND
                for hold/not-qualified records that want an optional slot.
                Hidden for replacement clients (they never take a doctor slot). */}
            {formClientType !== "replacement" && (slotDialog?.appointment || slotDialog?.__new || formStatus === "hold" || formStatus === "not_qualified") && (
              <div style={{
                background: "rgba(15,59,46,0.04)",
                border: `1px solid rgba(15,59,46,0.15)`,
                borderRadius: 10,
                padding: 12,
              }}>
                <Label className="text-xs" style={{ letterSpacing: "0.06em", textTransform: "uppercase", color: LABEL_GREEN }}>
                  {(formStatus === "hold" || formStatus === "not_qualified")
                    ? "Appointment date & time (optional)"
                    : (slotDialog?.__new && !slotDialog?.appointment ? "Appointment date & time" : "Reschedule")}
                </Label>
                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 8, marginTop: 8 }}>
                  {/* New date */}
                  <Popover open={apptDateOpen} onOpenChange={setApptDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="consult-reschedule-date-trigger"
                        style={{
                          height: 40, width: "100%", textAlign: "left",
                          display: "flex", alignItems: "center", gap: 6,
                          padding: "0 12px",
                          borderRadius: 8, border: `1px solid ${BORDER}`,
                          background: "#FFFFFF", color: TEXT,
                          fontSize: 14, fontFamily: "inherit", cursor: "pointer",
                        }}
                      >
                        <CalendarIcon style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {formApptDate ? prettyDateShort(formApptDate) : "Pick a date"}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="w-auto p-2" data-testid="consult-reschedule-date-popover">
                      <Calendar
                        mode="single"
                        selected={formApptDate ? fromYMD(formApptDate) : undefined}
                        onSelect={(d) => {
                          if (d) {
                            const newYmd = toYMD(d);
                            setFormApptDate(newYmd);
                            setApptDateOpen(false);
                            // Auto-flag as "Rescheduled" if the target date
                            // differs from the original booking.
                            if (slotDialog?.appointment &&
                                newYmd !== slotDialog.appointment.date) {
                              setFormClientType("rescheduled");
                            }
                          }
                        }}
                        modifiers={modifiers}
                        modifiersClassNames={modifiersClassNames}
                        className="p-0"
                      />
                    </PopoverContent>
                  </Popover>
                  {/* New time */}
                  <Select
                    value={formApptSlot || "__none__"}
                    onValueChange={(v) => {
                      const newSlot = v === "__none__" ? "" : v;
                      setFormApptSlot(newSlot);
                      // Auto-flag as "Rescheduled" if the target time differs
                      // from the original booking's time (same or different day).
                      if (slotDialog?.appointment &&
                          (newSlot !== slotDialog.appointment.slot_time ||
                           formApptDate !== slotDialog.appointment.date)) {
                        setFormClientType("rescheduled");
                      }
                    }}
                    disabled={rescheduleLoading || rescheduleSlots.length === 0}
                  >
                    <SelectTrigger
                      data-testid="consult-reschedule-time"
                      className="h-10 bg-white min-w-0 [&>span]:truncate"
                    >
                      <SelectValue placeholder={rescheduleLoading ? "Loading…" : "Pick a time"} />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {rescheduleSlots.length === 0 && (
                        <SelectItem value="__none__" disabled>
                          {rescheduleLoading ? "Loading…" : "No availability set for this day"}
                        </SelectItem>
                      )}
                      {rescheduleSlots.map((s) => (
                        <SelectItem
                          key={s.slot_time}
                          value={s.slot_time}
                          disabled={s.taken}
                        >
                          {prettyTime(s.slot_time)}
                          {s.taken ? " · booked" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {slotDialog.appointment && formApptDate === slotDialog.appointment.date &&
                 formApptSlot === slotDialog.appointment.slot_time ? (
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: MUTED }}>
                    Currently on <b>{prettyDateShort(slotDialog.appointment.date)}</b> at{" "}
                    <b>{prettyTime(slotDialog.appointment.slot_time)}</b>.
                  </p>
                ) : (slotDialog?.__new && !slotDialog?.appointment && (formApptDate || formApptSlot)) ? (
                  // New patient: no "Booked for …" line — the date & time
                  // pickers right above already show (and change) it.
                  null
                ) : (formApptDate || formApptSlot) ? (
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: DARK_GREEN, fontWeight: 600 }}>
                    {(formStatus === "hold" || formStatus === "not_qualified") ? "Slot: " : (slotDialog?.__new && !slotDialog?.appointment ? "Booked for " : "Moving to ")}
                    <b>{formApptDate ? prettyDateShort(formApptDate) : "—"}</b> at{" "}
                    <b>{formApptSlot ? prettyTime(formApptSlot) : "—"}</b>.
                    {" "}<span style={{ color: MUTED, fontWeight: 400 }}>Save to confirm.</span>
                  </p>
                ) : (
                  <p style={{ margin: "8px 0 0", fontSize: 12, color: MUTED }}>
                    Leave both empty to keep this as a placeholder without a slot.
                  </p>
                )}
                {((formStatus === "hold" || formStatus === "not_qualified") || slotDialog?.__new) && (formApptDate || formApptSlot) && (
                  <button
                    type="button"
                    data-testid="consult-clear-hold-slot"
                    onClick={() => { setFormApptDate(""); setFormApptSlot(""); }}
                    style={{
                      marginTop: 8, background: "transparent", border: 0,
                      color: MUTED, fontSize: 12, fontWeight: 600,
                      textDecoration: "underline", cursor: "pointer", padding: 0,
                    }}
                  >
                    Clear date &amp; time
                  </button>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="patient-name">Patient name</Label>
              <Input
                id="patient-name"
                data-testid="consult-patient-name"
                autoFocus
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="e.g., Jane Doe"
              />
            </div>

            {/* DOB */}
            <div>
              <Label>Date of birth (DOB)</Label>
              <div style={{ marginTop: 4 }}>
                <Popover open={dobOpen} onOpenChange={setDobOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="consult-dob-trigger"
                      style={{
                        height: 40,
                        display: "flex", alignItems: "center", justifyContent: "space-between",
                        gap: 6, padding: "0 12px",
                        borderRadius: 8, border: `1px solid ${BORDER}`,
                        background: "#FFFFFF", color: formDOB ? TEXT : "#8A9691",
                        fontSize: 14, fontFamily: "inherit", cursor: "pointer",
                        width: "100%", textAlign: "left",
                      }}
                    >
                      <span style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
                        <CalendarIcon style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
                        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {formDOB ? prettyDateShort(formDOB) : "Select date of birth"}
                        </span>
                      </span>
                      {formDOB && (
                        <span
                          role="button"
                          tabIndex={0}
                          data-testid="consult-dob-clear"
                          onClick={(e) => { e.stopPropagation(); setFormDOB(""); }}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); setFormDOB(""); } }}
                          style={{ color: MUTED, display: "flex", alignItems: "center", padding: 2, cursor: "pointer" }}
                          aria-label="Clear date of birth"
                        >
                          <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                        </span>
                      )}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-3" data-testid="consult-dob-popover">
                    <DobPicker
                      value={formDOB}
                      onChange={(v) => { setFormDOB(v); setDobOpen(false); }}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* HCN */}
            <div>
              <Label htmlFor="patient-hcn">Health Card Number (HCN)</Label>
              <Input
                id="patient-hcn"
                data-testid="consult-hcn"
                value={formHCN}
                onChange={(e) => setFormHCN(formatHCN(e.target.value))}
                placeholder="1234567890 XX"
                inputMode="text"
                autoComplete="off"
                maxLength={13}
                className="mt-1 font-mono uppercase"
              />
            </div>

            {/* Registration date — immutable through reschedules so staff
                can always see the ORIGINAL scheduled date. */}
            <div>
              <Label>Registration date</Label>
              <div style={{ marginTop: 4 }}>
                <Popover open={regDateOpen} onOpenChange={setRegDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="consult-regdate-trigger"
                      style={{
                        height: 40, width: "100%", textAlign: "left",
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "0 12px",
                        borderRadius: 8, border: `1px solid ${BORDER}`,
                        background: "#FFFFFF", color: TEXT,
                        fontSize: 14, fontFamily: "inherit", cursor: "pointer",
                      }}
                    >
                      <CalendarIcon style={{ width: 14, height: 14, flexShrink: 0 }} strokeWidth={2} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {formRegDate ? prettyDateShort(formRegDate) : "Auto-set on save"}
                      </span>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-auto p-2" data-testid="consult-regdate-popover">
                    <Calendar
                      mode="single"
                      selected={formRegDate ? fromYMD(formRegDate) : undefined}
                      onSelect={(d) => {
                        if (d) {
                          setFormRegDate(toYMD(d));
                          setRegDateOpen(false);
                        }
                      }}
                      className="p-0"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <p style={{ margin: "4px 0 0", fontSize: 11, color: MUTED, lineHeight: 1.5 }}>
                Defaults to today when the record is first created.
                <br />
                Persists through reschedules.
              </p>
            </div>

            {/* Copy RE / PHN / DOB */}
            <div>
              <button
                type="button"
                data-testid="consult-refhcn-copy"
                disabled={!formName.trim() && !formHCN.trim() && !formDOB}
                onClick={async () => {
                  const dobPart = formDOB ? prettyDateShort(formDOB) : "";
                  const text =
                    `RE: ${formName.trim() || "—"}\n` +
                    `PHN: ${formHCN.trim() || "—"}\n` +
                    `DOB: ${dobPart || "—"}`;
                  const ok = await copyPlainText(text);
                  if (ok) toast.success("Patient reference copied to clipboard.");
                  else toast.error("Copy failed — please copy manually.");
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  width: "100%", height: 36,
                  fontSize: 13, fontWeight: 600,
                  color: (formName.trim() || formHCN.trim() || formDOB) ? DARK_GREEN : "#B4BDB8",
                  background: (formName.trim() || formHCN.trim() || formDOB) ? "rgba(15,59,46,0.06)" : "transparent",
                  border: `1px solid ${(formName.trim() || formHCN.trim() || formDOB) ? "rgba(15,59,46,0.25)" : BORDER}`,
                  borderRadius: 8, padding: "0 12px",
                  cursor: (formName.trim() || formHCN.trim() || formDOB) ? "pointer" : "default",
                  fontFamily: "inherit",
                }}
              >
                <Copy style={{ width: 13, height: 13 }} strokeWidth={2} />
                Copy RE / PHN / DOB to clipboard
              </button>
            </div>

            {/* Client type toggle */}
            <div>
              <Label>Client type</Label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8, marginTop: 4 }}>
                {[
                  { v: "new", label: "New client", col: NEW_COL },
                  { v: "rescheduled", label: "Rescheduled", col: RESCHED_COL },
                  { v: "replacement", label: "Replacement", col: "#4A6FB0" },
                ].map((opt) => {
                  // Lock the type only for BRAND-NEW hold intake (header Hold
                  // button). Existing hold records can be freely re-typed.
                  const holdLocked = slotDialog?.__hold && !slotDialog?.appointment;
                  const disabled = holdLocked && opt.v !== "new";
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        const prev = formClientType;
                        setFormClientType(opt.v);
                        // Replacement auto-defaults status to "not_required"
                        // and expects a duration. Referral is NOT touched —
                        // the user may still want to send a specialist
                        // referral for a replacement client.
                        if (opt.v === "replacement") {
                          setFormStatus("not_required");
                          if (!formReplacementMonths) setFormReplacementMonths(1);
                        } else if (prev === "replacement") {
                          // Silent reset — user opted for speed over confirm
                          if (formStatus === "not_required") setFormStatus("scheduled");
                          setFormReplacementMonths(0);
                        }
                      }}
                      data-testid={`consult-client-type-${opt.v}`}
                      style={{
                        ...togglePill(formClientType === opt.v, opt.col),
                        ...(disabled ? { opacity: 0.35, cursor: "not-allowed" } : {}),
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {slotDialog?.__hold && !slotDialog?.appointment && (
                <p style={{ fontSize: 11, color: "#7A4400", marginTop: 6, fontWeight: 500 }}>
                  Locked to <b>New client</b> — holds are new patients pending qualification.
                </p>
              )}
            </div>

            {/* Replacement duration (only when Replacement selected) */}
            {formClientType === "replacement" && (
              <div>
                <Label>Replacement duration <span style={{ color: MISSED_COL }}>*</span></Label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 4 }}>
                  {[1, 2, 3].map((m) => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setFormReplacementMonths(m)}
                      data-testid={`consult-repl-months-${m}`}
                      style={togglePill(formReplacementMonths === m, "#4A6FB0")}
                    >
                      {m} month{m === 1 ? "" : "s"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Case management — the pipeline stage. Happens before the
                drug is dispensed, so it sits ABOVE Medication as its own
                labeled field. "Pending" (first option) = no stage selected,
                matches the Pending row in Stats. */}
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "nowrap" }}>
                <Label htmlFor="case-management">Case management</Label>
                {(perms.isAdmin || perms.case_stages_admin) && !previewStaff && (
                  <button
                    type="button"
                    data-testid="consult-manage-stages"
                    onClick={() => setManageStagesOpen(true)}
                    style={{ background: "none", border: "none", padding: 0, fontSize: 11, fontWeight: 700, color: "#1F4E79", textDecoration: "underline", cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap", flexShrink: 0 }}
                  >
                    Manage
                  </button>
                )}
              </div>
              <Select
                value={medStageValue}
                onValueChange={(v) => setMedStage(v === "__none__" ? "" : v)}
              >
                <SelectTrigger id="case-management" data-testid="consult-med-stage" className="h-10 bg-white mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__" data-testid="consult-med-stage-none">
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                      <span style={{ width: 8, height: 8, borderRadius: 2, background: "#E58A2E", flexShrink: 0 }} />
                      Pending
                    </span>
                  </SelectItem>
                  {caseStages.map((s) => (
                    <SelectItem key={s.key} value={s.key} data-testid={`consult-med-stage-${s.key}`}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 7 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0 }} />
                        {s.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Medication (mandatory) */}
            <div>
              <Label htmlFor="medication">
                Medication {medStageNeedsDrug && <span style={{ color: MISSED_COL }}>*</span>}
              </Label>
              <Select
                value={formMedication || "__none__"}
                onValueChange={(v) => setFormMedication(v === "__none__" ? "" : v)}
              >
                <SelectTrigger
                  id="medication"
                  data-testid="consult-medication"
                  className="h-10 bg-white mt-1"
                  style={medStageNeedsDrug && !formMedication ? { borderColor: MISSED_COL, boxShadow: `0 0 0 1px ${MISSED_COL}22 inset` } : {}}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Please select —</SelectItem>
                  {medications.map((m) => (
                    <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div style={{ marginTop: 4, textAlign: "right" }}>
                <button
                  type="button"
                  data-testid="consult-manage-meds"
                  onClick={() => setManageMedsOpen(true)}
                  style={{
                    background: "none", border: 0, padding: 0,
                    color: DARK_GREEN, fontSize: 11, fontWeight: 600,
                    cursor: "pointer", textDecoration: "underline",
                  }}
                >
                  Manage medications
                </button>
              </div>
            </div>

            {/* Refills — Yes requires the next refill date; tracked under
                the Pharmacy tab with a dispensed toggle. */}
            <div>
              <Label>Refills</Label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
                {[
                  { v: false, label: "No", tid: "consult-refill-no" },
                  { v: true, label: "Yes", tid: "consult-refill-yes" },
                ].map((opt) => (
                  <button
                    key={opt.tid}
                    type="button"
                    onClick={() => setFormRefill(opt.v)}
                    data-testid={opt.tid}
                    style={togglePill(formRefill === opt.v, DARK_GREEN)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {formRefill && (
                <div style={{ marginTop: 8 }}>
                  <Label>
                    Next refill date <span style={{ color: MISSED_COL }}>*</span>
                  </Label>
                  <Popover open={refillDateOpen} onOpenChange={setRefillDateOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="consult-refill-date-trigger"
                        style={{
                          ...inputStyle,
                          marginTop: 4,
                          textAlign: "left",
                          display: "flex", alignItems: "center", gap: 8,
                          cursor: "pointer",
                          ...(!formRefillDate ? { borderColor: MISSED_COL, boxShadow: `0 0 0 1px ${MISSED_COL}22 inset` } : {}),
                        }}
                      >
                        <CalendarIcon style={{ width: 15, height: 15, color: MUTED }} strokeWidth={2} />
                        {formRefillDate
                          ? prettyDateShort(formRefillDate)
                          : <span style={{ color: MUTED }}>Select refill date</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent align="start" className="p-0 w-auto">
                      <Calendar
                        mode="single"
                        selected={formRefillDate ? fromYMD(formRefillDate) : undefined}
                        onSelect={(d) => {
                          if (d) {
                            setFormRefillDate(toYMD(d));
                            setRefillDateOpen(false);
                          }
                        }}
                        className="p-2"
                      />
                    </PopoverContent>
                  </Popover>
                  <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                    Tracked under the Pharmacy tab until marked dispensed.
                  </p>
                </div>
              )}
            </div>

            {/* Referral type toggle */}
            <div>
              <Label>Referral type</Label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 4 }}>
                {[
                  { v: "outreach", label: "Outreach" },
                  { v: "pharmacy", label: "Pharmacy" },
                ].map((opt) => (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() =>
                      setFormReferralType(formReferralType === opt.v ? "" : opt.v)
                    }
                    data-testid={`consult-referral-type-${opt.v}`}
                    style={togglePill(formReferralType === opt.v, DARK_GREEN)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Referral sent — defaults to Review until changed */}
            <div>
              <Label>Referral sent</Label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(58px, 1fr))", gap: 6, marginTop: 4 }}>
                {[
                  { v: "completed",     label: "Yes",     col: COMPLETED_COL,    tid: "consult-referral-yes" },
                  { v: "review",        label: "Review",  col: "#4A6FB0",        tid: "consult-referral-review" },
                  { v: "not_completed", label: "Pending", col: REF_PENDING_COL,  tid: "consult-referral-pending" },
                  { v: "not_required",  label: "N/A",     col: NOT_REQUIRED_COL, tid: "consult-referral-notreq" },
                ].map((opt) => {
                  // Only lock referral options when the dialog was opened
                  // via the Hold button for a BRAND-NEW record. Replacement
                  // clients can freely toggle between Yes / Pending / Not
                  // required — the referral is independent of the top-up.
                  const holdLocked = slotDialog?.__hold && !slotDialog?.appointment;
                  const disabled = (holdLocked && opt.v !== "not_required");
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      disabled={disabled}
                      onClick={() => { if (!disabled) setFormReferralDone(opt.v); }}
                      data-testid={opt.tid}
                      style={{
                        ...togglePill(formReferralDone === opt.v, opt.col),
                        ...(disabled ? { opacity: 0.35, cursor: "not-allowed" } : {}),
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {slotDialog?.__hold && !slotDialog?.appointment && (
                <p style={{ fontSize: 11, color: "#7A4400", marginTop: 6, fontWeight: 500 }}>
                  Locked to <b>N/A</b> — new hold clients don&apos;t need a referral yet.
                </p>
              )}
            </div>

            {/* Status */}
            <div>
              <Label>Status</Label>
              <div style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(96px, 1fr))",
                gap: 6,
                marginTop: 4,
              }}>
                {[
                  { v: "scheduled", label: "Scheduled", col: DARK_GREEN },
                  { v: "completed", label: "Completed", col: COMPLETED_COL },
                  { v: "missed", label: "Missed", col: MISSED_COL },
                  { v: "hold", label: "Hold", col: "#E58A2E" },
                  { v: "not_qualified", label: "Not qualified", col: "#B4351B" },
                  { v: "not_required", label: "N/A", col: NOT_REQUIRED_COL },
                ].map((opt) => {
                  // Brand-new hold intake stays locked to Hold/Not qualified.
                  // EXISTING hold records unlock every status so staff can
                  // toggle a client back to Scheduled (date+slot required at
                  // save) without deleting and recreating the file.
                  const holdLocked = slotDialog?.__hold && !slotDialog?.appointment;
                  const replacementLocked = formClientType === "replacement";
                  const disabled = (holdLocked && opt.v !== "hold" && opt.v !== "not_qualified")
                    || (replacementLocked && opt.v !== "not_required" && opt.v !== "not_qualified");
                  return (
                    <button
                      key={opt.v}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        setFormStatus(opt.v);
                        // For NEW hold records (opened via header Hold btn),
                        // also force Referral to Not required. When toggling
                        // Hold on an EXISTING scheduled appointment, keep
                        // whatever referral state the record already had —
                        // the user asked to keep referral options available
                        // for scheduled→hold conversions.
                        if (opt.v === "hold" && slotDialog?.__hold && !slotDialog?.appointment) {
                          setFormReferralDone("not_required");
                        }
                      }}
                      data-testid={`consult-status-${opt.v}`}
                      style={{
                        ...togglePill(formStatus === opt.v, opt.col),
                        ...(disabled ? { opacity: 0.35, cursor: "not-allowed" } : {}),
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              {slotDialog?.__hold && !slotDialog?.appointment && (
                <p style={{ fontSize: 11, color: "#7A4400", marginTop: 6, fontWeight: 500 }}>
                  Locked to <b>Hold</b> — you can add an optional appointment date &amp; time above.
                </p>
              )}
              {slotDialog?.__hold && slotDialog?.appointment && formStatus === "hold" && (
                <p style={{ fontSize: 11, color: "#7A4400", marginTop: 6, fontWeight: 500 }}>
                  This client is on hold — pick a date &amp; time above, then switch the status back to <b>Scheduled</b>.
                </p>
              )}
              {formClientType === "replacement" && (
                <p style={{ fontSize: 11, color: "#3A5580", marginTop: 6, fontWeight: 500 }}>
                  {formStatus === "not_qualified"
                    ? "Not qualified — replacement count is removed once you save."
                    : "Default is N/A — replacement clients don\u2019t need a consultation. Pick Not qualified if the client was screened out."}
                </p>
              )}
            </div>

            {/* Reason picker — shown only for hold / not_qualified. The list is
                edited via the small "Manage list" dialog opened by the ⋯ button. */}
            {(formStatus === "hold" || formStatus === "not_qualified") && (
              <div>
                <Label htmlFor="consult-reason">
                  Reason <span style={{ color: MUTED, fontWeight: 400 }}>(optional)</span>
                </Label>
                <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
                  <Select
                    value={formReason || "__none__"}
                    onValueChange={(v) => setFormReason(v === "__none__" ? "" : v)}
                  >
                    <SelectTrigger id="consult-reason" data-testid="consult-reason-select" className="h-10 bg-white" style={{ flex: 1 }}>
                      <SelectValue placeholder="Pick a reason…" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      <SelectItem value="__none__">— None —</SelectItem>
                      {(formStatus === "hold" ? holdReasons : nqReasons).map((r) => (
                        <SelectItem key={r.id} value={r.label} data-testid={`consult-reason-opt-${r.id}`}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid="consult-manage-reasons-btn"
                    onClick={() => setManageReasonsFor(formStatus)}
                    title="Manage the reason list"
                    style={{
                      height: 40, padding: "0 12px",
                      background: "rgba(15,59,46,0.06)",
                      border: `1px solid rgba(15,59,46,0.25)`,
                      color: DARK_GREEN, borderRadius: 8,
                      fontSize: 12, fontWeight: 700, cursor: "pointer",
                      whiteSpace: "nowrap", fontFamily: "inherit",
                    }}
                  >
                    Manage list
                  </button>
                </div>
              </div>
            )}

            {/* Scheduled by (required). Staff-scoped logins are locked to
                their own name — they never see the other staff. */}
            <div>
              <Label htmlFor="scheduled-by">
                Scheduled by <span style={{ color: MISSED_COL }}>*</span>
              </Label>
              {staffScope ? (
                <div
                  data-testid="consult-scheduled-by-locked"
                  className="h-10 mt-1 flex items-center px-3 rounded-md border border-slate-200 bg-slate-50 text-sm font-medium text-slate-700"
                >
                  {staffScope}
                </div>
              ) : (
              <>
              <Select
                value={formScheduledBy || "__none__"}
                onValueChange={(v) => { setFormScheduledBy(v === "__none__" ? "" : v); setFormStaffTouched(true); }}
                onOpenChange={(open) => { if (open) setFormStaffTouched(true); }}
              >
                <SelectTrigger
                  id="scheduled-by"
                  data-testid="consult-scheduled-by"
                  className="h-10 bg-white mt-1"
                  style={!formScheduledBy ? { borderColor: MISSED_COL, boxShadow: `0 0 0 1px ${MISSED_COL}22 inset` } : {}}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Please select —</SelectItem>
                  {staffNamesForMonth((formApptDate || selectedYMD).slice(0, 7), formScheduledBy).map((n) => (
                    <SelectItem key={n} value={n}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!formScheduledBy && (
                <p style={{
                  fontSize: 11, color: MISSED_COL, marginTop: 4,
                  fontWeight: 500,
                }}>
                  Required — pick who is booking this appointment.
                </p>
              )}
              </>
              )}
            </div>

            {/* Health card */}
            <div>
              <Label>Health card</Label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 4, marginTop: 4 }}>
                <button
                  type="button"
                  onClick={() => setFormHealthCard("yes")}
                  data-testid="consult-health-card-yes"
                  style={togglePill(formHealthCard === "yes", COMPLETED_COL)}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setFormHealthCard("unknown")}
                  data-testid="consult-health-card-unknown"
                  style={togglePill(formHealthCard === "unknown", "#6B7280")}
                >
                  Unknown
                </button>
                <button
                  type="button"
                  onClick={() => setFormHealthCard("no")}
                  data-testid="consult-health-card-no"
                  style={togglePill(formHealthCard === "no", REF_PENDING_COL)}
                >
                  <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                  No
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setFormHealthCard("recovered");
                    // Auto-set recovered date to today when the toggle flips
                    // and no date is on file yet.
                    if (!formRecoveredDate) setFormRecoveredDate(toYMD(new Date()));
                  }}
                  data-testid="consult-health-card-recovered"
                  style={togglePill(formHealthCard === "recovered", NEW_COL)}
                >
                  <Check style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                  Recovered
                </button>
              </div>
            </div>

            {/* Recovered date picker — only visible when Recovered is selected */}
            {formHealthCard === "recovered" && (
              <div>
                <Label>Health card recovered on</Label>
                <Popover open={recoveredDateOpen} onOpenChange={setRecoveredDateOpen}>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      data-testid="consult-recovered-date-trigger"
                      style={{
                        ...inputStyle,
                        marginTop: 4,
                        textAlign: "left",
                        display: "flex", alignItems: "center", gap: 8,
                        cursor: "pointer",
                      }}
                    >
                      <CalendarIcon style={{ width: 15, height: 15, color: MUTED }} strokeWidth={2} />
                      {formRecoveredDate
                        ? prettyDateShort(formRecoveredDate)
                        : <span style={{ color: MUTED }}>Select recovery date</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="p-0 w-auto">
                    <Calendar
                      mode="single"
                      selected={formRecoveredDate ? fromYMD(formRecoveredDate) : undefined}
                      onSelect={(d) => {
                        if (d) {
                          setFormRecoveredDate(toYMD(d));
                          setRecoveredDateOpen(false);
                        }
                      }}
                      className="p-2"
                    />
                  </PopoverContent>
                </Popover>
                <p style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                  Used for invoicing — appears on the month this date falls in.
                </p>
              </div>
            )}

            <div>
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                gap: 8, marginBottom: 4,
              }}>
                <Label htmlFor="patient-notes">Consultation notes</Label>
                <button
                  type="button"
                  data-testid="consult-notes-copy"
                  disabled={!formNotes.trim()}
                  onClick={async () => {
                    const ok = await copyPlainText(formNotes);
                    if (ok) toast.success("Consultation notes copied to clipboard.");
                    else toast.error("Copy failed — please copy manually.");
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 4,
                    fontSize: 12, fontWeight: 600,
                    color: formNotes.trim() ? DARK_GREEN : "#B4BDB8",
                    background: "transparent",
                    border: `1px solid ${formNotes.trim() ? "rgba(15,59,46,0.25)" : BORDER}`,
                    borderRadius: 6, padding: "4px 8px",
                    cursor: formNotes.trim() ? "pointer" : "default",
                    fontFamily: "inherit",
                  }}
                >
                  <Copy style={{ width: 12, height: 12 }} strokeWidth={2} />
                  Copy
                </button>
              </div>
              <Textarea
                id="patient-notes"
                data-testid="consult-patient-notes"
                rows={3}
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
              />
            </div>

            <div style={{
              display: "flex", alignItems: "center", justifyContent: "flex-end",
              gap: 8, paddingTop: 4,
            }}>
              {slotDialog?.appointment && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  data-testid="consult-slot-delete"
                  onClick={() => setConfirmDelete(slotDialog.appointment)}
                  className="text-red-600 border-red-200 hover:bg-red-50 h-9 px-3 mr-auto"
                >
                  <Trash2 className="h-4 w-4 mr-1" />
                  Delete
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={closeSlot}
                data-testid="consult-slot-cancel"
                className="h-9 px-3"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                data-testid="consult-slot-save"
                disabled={savingSlot}
                className="h-9 px-4"
                style={{ background: DARK_GREEN, color: "white" }}
              >
                {savingSlot ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="consult-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this appointment?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete && (
                <>
                  This will free up the <b>{prettyTime(confirmDelete.slot_time)}</b> slot on {prettyDateShort(confirmDelete.date)}.
                  <br /><br />
                  Patient: <b>{confirmDelete.patient_name}</b>
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="consult-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && deleteAppt(confirmDelete)}
              data-testid="consult-confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manage medications dialog */}
      <ManageMedicationsDialog
        open={manageMedsOpen}
        onOpenChange={setManageMedsOpen}
        medications={medications}
        onChanged={loadMedications}
      />

      {/* Manage case-management stages dialog (admin / case_stages_admin) */}
      <ManageCaseStagesDialog
        open={manageStagesOpen}
        onOpenChange={setManageStagesOpen}
        stages={caseStages}
        onChanged={() => {
          loadCaseStages();
          // Re-ordering changes which bucket multi-stage records land in —
          // reload stats so EVERY staff card reflects the new pipeline
          // order immediately, not just after a month switch.
          if (tab === "stats") loadStats();
        }}
        onOpenFile={async (fileId) => {
          // Open the flagged file straight from the blocked-delete list.
          try {
            const { data } = await api.get(`/appointments/${fileId}`);
            if (data?.date) setSelectedDate(fromYMD(data.date));
            setManageStagesOpen(false);
            setSlotDialog(null);
            openSlot({ slot_time: data.slot_time || "09:00", appointment: data });
          } catch {
            toast.error("Could not load the patient record — try again.");
          }
        }}
      />

      {/* Manage reasons dialog (hold + not_qualified share this component) */}
      <ManageReasonsDialog
        open={!!manageReasonsFor}
        type={manageReasonsFor || "hold"}
        onOpenChange={(o) => !o && setManageReasonsFor(null)}
        reasons={manageReasonsFor === "not_qualified" ? nqReasons : holdReasons}
        onChanged={loadReasons}
      />

      {/* Stats drill-down list */}
      <Dialog open={!!statsDetail} onOpenChange={(o) => !o && closeStatsDetail()}>
        <DialogContent data-testid="consult-stats-detail-dialog" className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader className="text-left sm:text-left pl-3">
            <DialogTitle data-testid="consult-stats-detail-title" className="text-left">
              {statsDetail?.label || ""}
            </DialogTitle>
            <DialogDescription asChild>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span style={{ color: TEXT, fontWeight: 500 }}>
                  {statsRange
                    ? `${prettyDateShort(statsRange.from)} – ${prettyDateShort(statsRange.to)}`
                    : format(fromYMD(`${statsMonth}-01`), "MMMM yyyy")}
                </span>
                {statsDetail?.loading ? (
                  <span>Loading appointments…</span>
                ) : (
                  <>
                    <span>
                      {(statsDetail?.items || []).length} appointment
                      {(statsDetail?.items || []).length === 1 ? "" : "s"}.
                    </span>
                    <span>Click a row to open the profile.</span>
                  </>
                )}
              </div>
            </DialogDescription>
          </DialogHeader>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {!statsDetail?.loading && (statsDetail?.items || []).length === 0 && (
              <p style={{ fontSize: 13, color: MUTED, padding: "16px 4px" }}>
                No appointments match this segment for the selected month.
              </p>
            )}
            {(statsDetail?.items || []).map((a) => (
              <button
                key={a.id}
                type="button"
                data-testid={`consult-stats-detail-row-${a.id}`}
                onClick={() => {
                  // Keep user on the Stats tab. openApptFromStats stashes the
                  // current drill-down state so closeSlot can restore it.
                  openApptFromStats(a);
                }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 12,
                  padding: "10px 12px",
                  background: "white",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  textAlign: "left", fontFamily: "inherit",
                  cursor: "pointer",
                  transition: "background 120ms ease, border-color 120ms ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = "#F4F3EF";
                  e.currentTarget.style.borderColor = "rgba(15,59,46,0.35)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "white";
                  e.currentTarget.style.borderColor = BORDER;
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {a.patient_name || "—"}
                  </div>
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                    {(() => {
                      // The four status boxes (Qualified / Hold / Not qualified /
                      // Replacement) show each client's STATUS instead of the
                      // date, so the list itself answers "why/where is this
                      // client" without opening every file. `_row_mode` is set
                      // by those pills; every other drill-down (stage rows,
                      // drug rows, day clicks…) keeps the date/time line.
                      const mode = statsDetail?.params?._row_mode;
                      if (mode === "hcn") {
                        // Case-management drill-downs (stage rows, Pending,
                        // by-drug): the category is already the dialog title,
                        // so each row is just the client + their HCN — open
                        // the file for anything more.
                        return a.hcn
                          ? <span data-testid={`consult-stats-detail-hcn-${a.id}`}>{a.hcn}</span>
                          : <span style={{ fontStyle: "italic" }}>No health card on file</span>;
                      }
                      if (mode === "qualified") {
                        // Furthest ticked case-management stage (list order =
                        // pipeline order); nothing ticked = Pending.
                        let stg = null;
                        for (let i = caseStages.length - 1; i >= 0; i--) {
                          if (a[caseStages[i].key] === true) { stg = caseStages[i]; break; }
                        }
                        return stg
                          ? <span style={{ color: stg.color || DARK_GREEN, fontWeight: 600 }}>{stg.label}</span>
                          : <span style={{ color: "#E58A2E", fontWeight: 600 }}>Pending</span>;
                      }
                      if (mode === "hold" || mode === "not_qualified") {
                        const col = mode === "hold" ? "#E58A2E" : "#B4351B";
                        return a.reason
                          ? <span style={{ color: col, fontWeight: 600 }}>{a.reason}</span>
                          : <span style={{ fontStyle: "italic" }}>No reason logged</span>;
                      }
                      if (mode === "replacement") {
                        const n = a.replacement_months || 0;
                        return n > 0
                          ? <span style={{ color: "#4A6FB0", fontWeight: 600 }}>{n} month{n > 1 ? "s" : ""}</span>
                          : <span style={{ fontStyle: "italic" }}>Duration not set</span>;
                      }
                      return a.status === "not_qualified"
                        ? <span style={{ color: "#B4351B", fontWeight: 600 }}>Not qualified · {a.date ? `${prettyDateShort(a.date)} · ${prettyTime(a.slot_time)}` : "no date/time"}</span>
                        : a.status === "hold"
                          ? <span style={{ color: "#E58A2E", fontWeight: 600 }}>Hold · {a.date ? `${prettyDateShort(a.date)} · ${prettyTime(a.slot_time)}` : "no date/time"}</span>
                          : a.date
                            ? <>{prettyDateShort(a.date)} · {prettyTime(a.slot_time)}</>
                            : a.client_type === "replacement"
                              ? <span style={{ color: "#4A6FB0", fontWeight: 600 }}>Replacement · no date/time</span>
                              : <span style={{ color: "#E58A2E", fontWeight: 600 }}>Hold · no date/time</span>;
                    })()}
                  </div>
                  {/* PHN line — suppressed in "hcn" mode, where the second
                      line already IS the health card number. */}
                  {a.hcn && statsDetail?.params?._row_mode !== "hcn" && (
                    <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>
                      PHN {a.hcn}
                    </div>
                  )}
                </div>
                <ChevronRight style={{ width: 16, height: 16, color: MUTED, flexShrink: 0 }} />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        @media (max-width: 900px) {
          .consult-grid { grid-template-columns: 1fr !important; }
        }
        /* Mobile: let the tab bar scroll horizontally instead of squashing */
        @media (max-width: 640px) {
          .consult-tabs {
            flex-wrap: nowrap !important;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            scrollbar-width: none;
          }
          .consult-tabs::-webkit-scrollbar { display: none; }
          .consult-tabs > button {
            flex: 0 0 auto !important;
            min-width: auto !important;
            padding: 8px 12px !important;
          }
          .consult-tabs > button span {
            max-width: none !important;
          }
        }
      `}</style>
    </div>
  );
}

// ==================== subcomponents ====================
const tabBtn = (active) => ({
  display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
  background: active ? DARK_GREEN : "transparent",
  color: active ? "white" : MUTED,
  border: active ? "none" : `1px solid ${BORDER}`,
  borderRadius: 8, padding: "8px 10px",
  fontSize: 13, fontWeight: 600, cursor: "pointer",
  flex: "1 1 0",
  minWidth: 0,
});

const labelStyle = {
  fontSize: 12, fontWeight: 700, letterSpacing: "0.24em",
  textTransform: "uppercase", color: LABEL_GREEN, margin: 0,
};

const inputStyle = {
  width: "100%", height: 40, borderRadius: 8, padding: "0 12px",
  fontSize: 14, background: "#FFFFFF", border: `1px solid ${BORDER}`,
  outline: "none", color: TEXT, fontFamily: "inherit", boxSizing: "border-box",
};

const togglePill = (active, activeColor) => ({
  display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
  padding: "8px 6px",
  borderRadius: 8,
  fontSize: 12, fontWeight: 600,
  border: `1.5px solid ${active ? activeColor : BORDER}`,
  background: active ? activeColor : "white",
  color: active ? "white" : MUTED,
  cursor: "pointer",
  transition: "background .15s, color .15s, border-color .15s",
  whiteSpace: "nowrap",
  minWidth: 0,
});

function StatusPill({ status }) {
  const cfg = {
    scheduled: { bg: "rgba(15,59,46,0.10)", fg: DARK_GREEN, label: "Scheduled", Icon: Clock },
    completed: { bg: "rgba(46,125,91,0.14)", fg: COMPLETED_COL, label: "Completed", Icon: Check },
    missed: { bg: "rgba(180,53,27,0.12)", fg: MISSED_COL, label: "Missed", Icon: XIcon },
    hold: { bg: "rgba(229,138,46,0.12)", fg: "#B46A12", label: "Hold", Icon: PauseCircle },
    not_qualified: { bg: "rgba(180,53,27,0.12)", fg: "#B4351B", label: "Not qualified", Icon: XIcon },
    not_required: { bg: "rgba(74,111,176,0.12)", fg: "#4A6FB0", label: "N/A", Icon: Check },
  }[status] || { bg: "rgba(15,59,46,0.10)", fg: DARK_GREEN, label: status, Icon: Clock };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: cfg.bg, color: cfg.fg, borderRadius: 999,
      padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
    }}>
      <cfg.Icon style={{ width: 10, height: 10 }} strokeWidth={2.5} />
      {cfg.label}
    </span>
  );
}

function ClientTypePill({ type }) {
  if (type === "rescheduled") {
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "rgba(138,90,0,0.10)", color: RESCHED_COL, borderRadius: 999,
        padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
      }}>
        <Repeat style={{ width: 10, height: 10 }} strokeWidth={2.5} />
        Rescheduled
      </span>
    );
  }
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4,
      background: "rgba(15,59,46,0.08)", color: DARK_GREEN, borderRadius: 999,
      padding: "2px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
    }}>
      <Sparkles style={{ width: 10, height: 10 }} strokeWidth={2.5} />
      New
    </span>
  );
}

function ReferralPill({ done }) {
  if (done) {
    return (
      <span
        data-testid="consult-referral-pill-done"
        style={{
          display: "inline-flex", alignItems: "center", gap: 4,
          background: "rgba(46,125,91,0.12)", color: COMPLETED_COL,
          borderRadius: 6,
          padding: "3px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
        }}
      >
        <FileCheck2 style={{ width: 11, height: 11 }} strokeWidth={2.5} />
        Referral completed
      </span>
    );
  }
  return (
    <span
      data-testid="consult-referral-pill-pending"
      style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        background: "rgba(180,53,27,0.10)", color: REF_PENDING_COL,
        borderRadius: 6,
        padding: "3px 8px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap",
        border: `1px dashed ${REF_PENDING_COL}55`,
      }}
    >
      <AlertCircle style={{ width: 11, height: 11 }} strokeWidth={2.5} />
      Referral pending
    </span>
  );
}

function SlotCard({ slot, onClick }) {
  const appt = slot.appointment;
  const isBooked = !!appt;
  const status = appt?.status || "scheduled";
  const borderColor = isBooked
    ? (status === "completed" ? "rgba(46,125,91,0.55)"
      : status === "missed"    ? "rgba(180,53,27,0.55)"
      : "rgba(15,59,46,0.35)")
    : BORDER;
  const bg = isBooked
    ? (status === "completed" ? "rgba(46,125,91,0.06)"
      : status === "missed"    ? "rgba(180,53,27,0.06)"
      : "rgba(15,59,46,0.06)")
    : "white";
  const timeColor = isBooked ? (status === "missed" ? MISSED_COL : (status === "completed" ? COMPLETED_COL : DARK_GREEN)) : TEXT;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`consult-slot-${slot.slot_time}`}
      data-booked={isBooked ? "true" : "false"}
      data-status={status}
      style={{
        textAlign: "left", borderRadius: 10,
        padding: "12px 14px",
        border: `1px solid ${borderColor}`, background: bg,
        cursor: "pointer", display: "flex", flexDirection: "column", gap: 4,
        minHeight: 82, transition: "border-color .15s, background .15s",
      }}
      onMouseEnter={(e) => { if (!isBooked) e.currentTarget.style.borderColor = DARK_GREEN; }}
      onMouseLeave={(e) => { if (!isBooked) e.currentTarget.style.borderColor = BORDER; }}
    >
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        fontSize: 13, fontWeight: 700, color: timeColor,
      }}>
        <span style={{
          display: "flex", alignItems: "center", gap: 6,
          whiteSpace: "nowrap", flexShrink: 0,
        }}>
          <Clock style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2.2} />
          {prettyTime(slot.slot_time)}
        </span>
        {isBooked && <StatusPill status={status} />}
      </div>
      {isBooked ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 4, marginTop: 2 }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            gap: 6, flexWrap: "wrap", rowGap: 4,
          }}>
            <div style={{
              fontSize: 14, fontWeight: 600, color: TEXT,
              display: "flex", alignItems: "center", gap: 6,
              minWidth: 0, flex: "1 1 120px",
            }}>
              <User style={{ width: 13, height: 13, color: LABEL_GREEN, flexShrink: 0 }} strokeWidth={2} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {appt.patient_name}
              </span>
            </div>
            <ClientTypePill type={appt.client_type || "new"} />
          </div>
          <ReferralPill done={!!appt.referral_completed} />
        </div>
      ) : (
        <div style={{
          fontSize: 12, color: MUTED,
          display: "flex", alignItems: "center", gap: 4, marginTop: 2,
        }}>
          <Plus style={{ width: 12, height: 12 }} strokeWidth={2.5} />
          Available
        </div>
      )}
    </button>
  );
}

function EmptyState({ icon, title, body }) {
  return (
    <div style={{
      padding: 40, textAlign: "center",
      border: `1px dashed ${BORDER}`, borderRadius: 12, background: "#FAFAF7",
    }}>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>{icon}</div>
      <p style={{ fontSize: 15, fontWeight: 600, color: TEXT, margin: "0 0 6px" }}>{title}</p>
      <p style={{ fontSize: 13, color: MUTED, margin: 0, maxWidth: 380, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
        {body}
      </p>
    </div>
  );
}

function ScheduledAuditPanel() {
  // Immutable historical log of every schedule + reschedule action.
  // Days collapse by default so the panel stays compact; users open just
  // the days they care about. A month dropdown at the top scopes the
  // list further when there are many months of history to sift through.
  const [entries, setEntries] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [openDays, setOpenDays] = React.useState({});           // { "YYYY-MM-DD": true }
  const [monthFilter, setMonthFilter] = React.useState("all");   // "all" | "YYYY-MM"

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments/audit");
      setEntries(Array.isArray(data?.entries) ? data.entries : []);
    } catch { setEntries([]); }
    finally { setLoading(false); }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  // Group entries by ISO day-of-action ("YYYY-MM-DD" from the `at`
  // timestamp). Newest day first because that's what the user asked for
  // ("most recent always at the top working down with each day").
  const grouped = React.useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      const day = (e.at || "").slice(0, 10);
      if (!day) continue;
      if (monthFilter !== "all" && day.slice(0, 7) !== monthFilter) continue;
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(e);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [entries, monthFilter]);

  // Distinct months represented in the audit trail, newest-first — used
  // to power the month dropdown.
  const monthOptions = React.useMemo(() => {
    const set = new Set();
    for (const e of entries) {
      const m = (e.at || "").slice(0, 7);
      if (m) set.add(m);
    }
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1));
  }, [entries]);

  const fmtMonth = (ym) => {
    if (!ym) return "";
    try {
      const d = new Date(ym + "-01T00:00:00");
      return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
    } catch { return ym; }
  };

  const fmtDay = (ymd) => {
    if (!ymd) return "";
    try {
      const d = new Date(ymd + "T00:00:00");
      return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    } catch { return ymd; }
  };
  const fmtSlot = (ymd, time) => {
    // 12-hour formatted slot line, e.g. "Aug 26, 2026 · 1:00 PM".
    if (!ymd) return "—";
    try {
      const iso = time && /^\d{1,2}:\d{2}$/.test(time)
        ? `${ymd}T${time.padStart(5, "0")}:00`
        : `${ymd}T00:00:00`;
      const d = new Date(iso);
      const dateLabel = d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
      if (!time) return dateLabel;
      const timeLabel = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
      return `${dateLabel} · ${timeLabel}`;
    } catch { return `${ymd}${time ? " · " + time : ""}`; }
  };
  const fmtWhen = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }); }
    catch { return ""; }
  };

  return (
    <div
      style={{
        border: `1px solid ${BORDER}`,
        borderRadius: 10,
        padding: 12,
        marginBottom: 12,
        background: "#FBFDFC",
      }}
      data-testid="consult-scheduled-audit"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="consult-scheduled-audit-toggle"
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          padding: 0,
          background: "transparent",
          border: 0,
          cursor: "pointer",
          textAlign: "left",
          fontFamily: "inherit",
        }}
      >
        <span style={{
          fontSize: 11,
          textTransform: "uppercase",
          letterSpacing: 1.5,
          fontWeight: 700,
          color: MUTED,
        }}>
          Scheduled
        </span>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: DARK_GREEN }}>
          {loading ? "…" : `${entries.length} action${entries.length === 1 ? "" : "s"}`}
          <ChevronRight
            style={{
              width: 14,
              height: 14,
              transition: "transform .15s ease",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
            }}
          />
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 10 }}>
          {monthOptions.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <label
                htmlFor="consult-scheduled-audit-month"
                style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: 1.2, fontWeight: 700, color: MUTED }}
              >
                Month
              </label>
              <select
                id="consult-scheduled-audit-month"
                data-testid="consult-scheduled-audit-month-select"
                value={monthFilter}
                onChange={(ev) => setMonthFilter(ev.target.value)}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: TEXT,
                  padding: "6px 10px",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 8,
                  background: "white",
                  fontFamily: "inherit",
                  cursor: "pointer",
                }}
              >
                <option value="all">All months</option>
                {monthOptions.map((m) => (
                  <option key={m} value={m}>{fmtMonth(m)}</option>
                ))}
              </select>
            </div>
          )}
          {grouped.length === 0 && !loading && (
            <p style={{ fontSize: 12, color: MUTED, margin: 0 }}>
              No scheduling actions yet. The log fills in as appointments get scheduled or rescheduled.
            </p>
          )}
          {grouped.map(([day, list]) => {
            const summaryText = [
              `${fmtDay(day)} — ${list.length} action${list.length === 1 ? "" : "s"}`,
              "",
              ...list.map((e) => {
                const lines = [
                  `${e.action === "rescheduled" ? "Rescheduled" : "Scheduled"} — ${e.patient_name || "—"}`,
                ];
                if (e.action === "rescheduled" && (e.previous_date || e.previous_time)) {
                  lines.push(`Was: ${fmtSlot(e.previous_date, e.previous_time)}`);
                  lines.push(`Now: ${fmtSlot(e.slot_date, e.slot_time)}`);
                } else {
                  lines.push(`When: ${fmtSlot(e.slot_date, e.slot_time)}`);
                }
                return lines.join("\n");
              }),
            ].join("\n\n");
            return (
            <div key={day} style={{ marginBottom: 6 }}>
              <button
                type="button"
                data-testid={`consult-scheduled-audit-day-toggle-${day}`}
                onClick={() => setOpenDays((m) => ({ ...m, [day]: !m[day] }))}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 8,
                  margin: 0,
                  padding: "6px 8px",
                  background: openDays[day] ? "rgba(15,59,46,0.04)" : "transparent",
                  border: `1px solid ${BORDER}`,
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  textAlign: "left",
                }}
              >
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                  <ChevronRight
                    style={{
                      width: 12,
                      height: 12,
                      color: MUTED,
                      flexShrink: 0,
                      transition: "transform .15s ease",
                      transform: openDays[day] ? "rotate(90deg)" : "rotate(0deg)",
                    }}
                  />
                  <span style={{
                    fontSize: 12,
                    fontWeight: 700,
                    color: TEXT,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {fmtDay(day)}
                  </span>
                </span>
                <span
                  data-testid={`consult-scheduled-audit-copy-${day}`}
                  role="button"
                  tabIndex={0}
                  title="Copy this day's summary"
                  onClick={async (ev) => {
                    ev.stopPropagation();
                    const ok = await copyPlainText(summaryText);
                    if (ok) toast.success(`Copied ${list.length} action${list.length === 1 ? "" : "s"} for ${fmtDay(day)}`);
                    else toast.error("Couldn't copy — try again");
                  }}
                  onKeyDown={async (ev) => {
                    if (ev.key !== "Enter" && ev.key !== " ") return;
                    ev.preventDefault();
                    ev.stopPropagation();
                    const ok = await copyPlainText(summaryText);
                    if (ok) toast.success(`Copied ${list.length} action${list.length === 1 ? "" : "s"} for ${fmtDay(day)}`);
                    else toast.error("Couldn't copy — try again");
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "3px 8px",
                    fontSize: 11,
                    fontWeight: 600,
                    color: DARK_GREEN,
                    background: "rgba(15,59,46,0.06)",
                    border: `1px solid rgba(15,59,46,0.20)`,
                    borderRadius: 6,
                    cursor: "pointer",
                    flexShrink: 0,
                  }}
                >
                  <Copy style={{ width: 11, height: 11 }} strokeWidth={2} />
                  Copy
                </span>
              </button>
              {openDays[day] && (
              <ul style={{ listStyle: "none", padding: 0, margin: "6px 0 0", display: "flex", flexDirection: "column", gap: 6 }}>
                {list.map((e) => (
                  <li
                    key={e.id}
                    data-testid={`consult-scheduled-audit-row-${e.id}`}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                      padding: "10px 12px",
                      border: `1px solid ${BORDER}`,
                      borderRadius: 8,
                      background: "white",
                      fontSize: 13,
                      lineHeight: 1.35,
                    }}
                  >
                    <span style={{
                      alignSelf: "flex-start",
                      padding: "2px 8px",
                      borderRadius: 999,
                      fontSize: 10,
                      fontWeight: 700,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                      background: e.action === "rescheduled" ? "#FEF3C7" : "#DCFCE7",
                      color:      e.action === "rescheduled" ? "#92400E" : "#166534",
                    }}>
                      {e.action}
                    </span>
                    <p style={{ margin: 0, fontWeight: 700, color: TEXT }}>
                      {e.patient_name || "—"}
                    </p>
                    {e.action === "rescheduled" && (e.previous_date || e.previous_time) && (
                      <p style={{ margin: 0, color: MUTED, textDecoration: "line-through" }}>
                        {fmtSlot(e.previous_date, e.previous_time)}
                      </p>
                    )}
                    <p style={{ margin: 0, color: TEXT }}>
                      {fmtSlot(e.slot_date, e.slot_time)}
                    </p>
                  </li>
                ))}
              </ul>
              )}
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function HistoryPanel({ items, loading, q, setQ, mode, setMode, statusFilter, setStatusFilter, referralFilter, setReferralFilter, referralSourceFilter, setReferralSourceFilter, showHolds, setShowHolds, onReload, onEdit, onDelete }) {
  const anyFilterActive =
    mode !== "all" || statusFilter !== "all" || referralFilter !== "all" ||
    referralSourceFilter !== "all" || q.trim() || showHolds;

  return (
    <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 20 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{
          fontFamily: "'Work Sans', system-ui, sans-serif",
          fontSize: 20, fontWeight: 700, margin: 0,
        }}>
          All appointments
        </h2>
        <p style={{ fontSize: 13, color: MUTED, margin: "4px 0 0" }}>
          {items.length} {items.length === 1 ? "result" : "results"}
        </p>
      </div>

      {/* Search bar */}
      <div style={{ position: "relative", marginBottom: 14 }}>
        <Search style={{
          position: "absolute", left: 12, top: "50%",
          transform: "translateY(-50%)", width: 15, height: 15, color: MUTED,
          pointerEvents: "none",
        }} />
        <input
          type="text"
          placeholder="Search patient name…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onReload()}
          data-testid="consult-history-search"
          style={{ ...inputStyle, paddingLeft: 36, height: 40 }}
        />
      </div>

      {/* Filter rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
        <ScheduledAuditPanel />
        <FilterRow
          label="When"
          value={mode}
          onChange={setMode}
          testPrefix="consult-history-filter"
          options={[
            { v: "all", label: "All" },
            { v: "upcoming", label: "Upcoming" },
            { v: "past", label: "Past" },
          ]}
        />
        <FilterRow
          label="Status"
          value={statusFilter}
          onChange={setStatusFilter}
          testPrefix="consult-history-status"
          options={[
            { v: "all", label: "All" },
            { v: "scheduled", label: "Scheduled" },
            { v: "completed", label: "Completed" },
            { v: "missed", label: "Missed" },
          ]}
        />
        {/* Show holds toggle */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "8px 12px", background: showHolds ? "rgba(229,138,46,0.10)" : "#F9F8F6",
          border: `1px solid ${showHolds ? "rgba(229,138,46,0.35)" : BORDER}`, borderRadius: 8,
        }}>
          <button
            type="button"
            data-testid="consult-history-show-holds"
            onClick={() => setShowHolds(!showHolds)}
            style={{
              width: 36, height: 20, borderRadius: 999,
              background: showHolds ? "#E58A2E" : "#D9D6D0",
              border: 0, cursor: "pointer", position: "relative", flexShrink: 0,
            }}
          >
            <span style={{
              position: "absolute", top: 2, [showHolds ? "right" : "left"]: 2,
              width: 16, height: 16, borderRadius: 999, background: "#fff",
              transition: "all 120ms ease",
            }} />
          </button>
          <div style={{ fontSize: 12, color: showHolds ? "#7A4400" : MUTED }}>
            <b>Show holds</b> — includes clients on hold in the list below
          </div>
        </div>
        <FilterRow
          label="Referred"
          value={referralFilter}
          onChange={setReferralFilter}
          testPrefix="consult-history-referral"
          options={[
            { v: "all", label: "All" },
            { v: "pending", label: "Pending" },
            { v: "completed", label: "Completed" },
          ]}
        />
        <FilterRow
          label="Source"
          value={referralSourceFilter}
          onChange={setReferralSourceFilter}
          testPrefix="consult-history-source"
          options={[
            { v: "all", label: "All" },
            { v: "outreach", label: "Outreach" },
            { v: "pharmacy", label: "Pharmacy" },
          ]}
        />
      </div>

      {anyFilterActive && (
        <div style={{ marginBottom: 14 }}>
          <button
            type="button"
            data-testid="consult-history-clear-filters"
            onClick={() => {
              setMode("all");
              setStatusFilter("all");
              setReferralFilter("all");
              setReferralSourceFilter("all");
              setQ("");
            }}
            style={{
              fontSize: 12, fontWeight: 600, color: DARK_GREEN,
              background: "rgba(15,59,46,0.06)",
              border: "1px solid rgba(15,59,46,0.20)",
              borderRadius: 999, padding: "5px 12px", cursor: "pointer",
              display: "inline-flex", alignItems: "center", gap: 4,
            }}
          >
            <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
            Clear filters
          </button>
        </div>
      )}

      <style>{`
        @media (max-width: 640px) {
          .consult-history-filter-row { flex-wrap: wrap; }
        }
      `}</style>

      {loading && items.length === 0 ? (
        <div style={{ color: MUTED, padding: 40, textAlign: "center" }}>Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CalendarDays style={{ width: 32, height: 32, color: MUTED }} />}
          title="No appointments found"
          body="Bookings you create will appear here — sorted by date, most recent first."
        />
      ) : (
        <div style={{ borderTop: `1px solid ${BORDER}` }}>
          {items.map((appt) => (
            <div
              key={appt.id}
              data-testid={`consult-history-item-${appt.id}`}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 4px", borderBottom: `1px solid ${BORDER}`,
                gap: 12,
              }}
            >
              <button
                type="button"
                onClick={() => onEdit(appt)}
                data-testid={`consult-history-row-${appt.id}`}
                style={{
                  flex: 1, minWidth: 0, textAlign: "left",
                  background: "transparent", border: "none", padding: 0,
                  cursor: "pointer", display: "flex", flexDirection: "column",
                  gap: 3, color: TEXT,
                }}
              >
                <span style={{
                  fontSize: 15, fontWeight: 600,
                  display: "flex", alignItems: "center", gap: 8,
                  minWidth: 0,
                }}>
                  <User style={{ width: 15, height: 15, color: LABEL_GREEN, flexShrink: 0 }} strokeWidth={2} />
                  <span style={{
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{appt.patient_name}</span>
                </span>
                <span style={{ fontSize: 13, color: MUTED, paddingLeft: 23 }}>
                  {prettyDateShort(appt.date)} · {prettyTime(appt.slot_time)}
                </span>
              </button>
              <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                <button
                  onClick={() => onEdit(appt)}
                  data-testid={`consult-history-edit-${appt.id}`}
                  style={iconBtn} title="View / edit"
                >
                  <Edit3 style={{ width: 15, height: 15 }} strokeWidth={2} />
                </button>
                <button
                  onClick={() => onDelete(appt)}
                  data-testid={`consult-history-delete-${appt.id}`}
                  style={{ ...iconBtn, color: MISSED_COL }} title="Delete"
                >
                  <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsPanel({ month, setMonth, range, setRange, stats, loading, onSegmentClick, onGoToOutreach, medications = [], staffFirst = null, staffShorts = STAFF_SHORTS, caseStages = [] }) {
  // Narrow viewports: staff stat cards take the full container width (same
  // size as the sections above/below); wider screens keep the 300px cards.
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && window.innerWidth <= 640);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  // Month navigator
  const shift = (delta) => {
    setRange(null); // switching to month mode
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const monthLabel = format(new Date(month + "-01"), "MMMM yyyy");
  const rangeLabel = range
    ? `${prettyDateShort(range.from)} – ${prettyDateShort(range.to)}`
    : monthLabel;
  const t = stats?.totals || { total: 0, completed: 0, missed: 0, scheduled: 0, hold: 0, not_required: 0, new: 0, rescheduled: 0, replacement: 0, outreach: 0, pharmacy: 0, referral_type_unset: 0, referral_completed: 0, referral_pending: 0, health_card_yes: 0, health_card_no: 0, health_card_recovered: 0, registered_in_period: 0, carried_over: 0 };
  const p = stats?.percentages || { completed: 0, missed: 0, scheduled: 0, hold: 0, not_required: 0, new: 0, rescheduled: 0, replacement: 0, outreach: 0, pharmacy: 0, referral_type_unset: 0, referral_completed: 0, referral_pending: 0, health_card_yes: 0, health_card_no: 0, health_card_recovered: 0, registered_in_period: 0, carried_over: 0 };
  // Every drill-down below is scoped to registration date. This helper
  // is spread into each segment's `filter` so the modal never shows
  // patients registered in a different month than the one being viewed.
  const RS = {
    _reg_scope: 1,
    reg_from: stats?.range?.start,
    reg_to: stats?.range?.end_exclusive,
    include_holds: 1,
  };
  const hasData = t.total > 0;

  // Local state for the custom-range popover.
  const [fromDraft, setFromDraft] = useState(range?.from || "");
  const [toDraft, setToDraft] = useState(range?.to || "");
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);
  useEffect(() => {
    setFromDraft(range?.from || "");
    setToDraft(range?.to || "");
  }, [range]);
  const canApply = !!fromDraft && !!toDraft && fromDraft <= toDraft;
  const applyRange = () => {
    if (!canApply) return;
    setRange({ from: fromDraft, to: toDraft });
  };
  const clearRange = () => {
    setRange(null);
    setFromDraft("");
    setToDraft("");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20, minWidth: 0, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Month navigator — outreach-card metrics + the label centred in a
          32px row so "Reporting month" lands exactly where "Staff member"
          does on the Calendar/MD/Inventory tabs. */}
      <div className="outreach-card" style={{
        background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
        padding: 20,
      }}>
        <div style={{
          display: "flex", alignItems: "center",
          justifyContent: "space-between", flexWrap: "wrap", gap: 12,
        }}>
          <div>
            <p style={{ ...labelStyle, margin: 0, display: "flex", alignItems: "center", minHeight: 32 }}>
              {range ? "Reporting range" : "Reporting month"}
            </p>
            <h2 style={{
              fontFamily: "'Work Sans', system-ui, sans-serif",
              fontSize: 24, fontWeight: 700, margin: 0,
            }} data-testid="consult-stats-month-label">{rangeLabel}</h2>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <button
              onClick={() => shift(-1)}
              data-testid="consult-stats-prev"
              style={monthNavBtn}
              disabled={!!range}
            >
              <ChevronLeft style={{ width: 16, height: 16 }} />
              Prev
            </button>
            <button
              onClick={() => {
                setRange(null);
                const d = new Date();
                setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
              }}
              data-testid="consult-stats-current"
              style={monthNavBtn}
            >
              This month
            </button>
            <button
              onClick={() => shift(1)}
              data-testid="consult-stats-next"
              style={monthNavBtn}
              disabled={!!range}
            >
              Next
              <ChevronRight style={{ width: 16, height: 16 }} />
            </button>
          </div>
        </div>

        {/* Custom date-range picker */}
        <div style={{
          borderTop: `1px dashed ${BORDER}`, marginTop: 14, paddingTop: 12,
        }}>
          <p style={{
            ...labelStyle, margin: "0 0 8px 0",
          }}>
            Custom range
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {/* Row 1: From | to | To */}
            <div style={{
              display: "grid",
              gridTemplateColumns: "1fr auto 1fr",
              alignItems: "center", gap: 6,
            }}>
              <Popover open={fromOpen} onOpenChange={setFromOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="consult-stats-from-trigger"
                    style={{ ...statsRangeBtn(fromDraft), width: "100%", justifyContent: "center" }}
                  >
                    <CalendarIcon style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2} />
                    {fromDraft ? prettyDateShort(fromDraft) : "From"}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-2">
                  <Calendar
                    mode="single"
                    selected={fromDraft ? fromYMD(fromDraft) : undefined}
                    onSelect={(d) => { if (d) { setFromDraft(toYMD(d)); setFromOpen(false); } }}
                    className="p-0"
                  />
                </PopoverContent>
              </Popover>
              <span style={{ fontSize: 13, color: MUTED }}>to</span>
              <Popover open={toOpen} onOpenChange={setToOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="consult-stats-to-trigger"
                    style={{ ...statsRangeBtn(toDraft), width: "100%", justifyContent: "center" }}
                  >
                    <CalendarIcon style={{ width: 13, height: 13, flexShrink: 0 }} strokeWidth={2} />
                    {toDraft ? prettyDateShort(toDraft) : "To"}
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-auto p-2">
                  <Calendar
                    mode="single"
                    selected={toDraft ? fromYMD(toDraft) : undefined}
                    onSelect={(d) => { if (d) { setToDraft(toYMD(d)); setToOpen(false); } }}
                    className="p-0"
                  />
                </PopoverContent>
              </Popover>
            </div>
            {/* Row 2: Apply (+ Clear when active) */}
            <div style={{ display: "flex", gap: 6 }}>
              <button
                type="button"
                onClick={applyRange}
                disabled={!canApply}
                data-testid="consult-stats-apply-range"
                style={{
                  background: canApply ? DARK_GREEN : "#E5E2DC",
                  color: canApply ? "white" : MUTED,
                  border: 0, borderRadius: 8, padding: "8px 12px",
                  fontSize: 13, fontWeight: 600,
                  cursor: canApply ? "pointer" : "default",
                  fontFamily: "inherit",
                  flex: 1,
                }}
              >
                Apply
              </button>
              {range && (
                <button
                  type="button"
                  onClick={clearRange}
                  data-testid="consult-stats-clear-range"
                  style={{
                    background: "transparent",
                    color: MUTED,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 8, padding: "8px 12px",
                    fontSize: 12, fontWeight: 600,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                  }}
                >
                  <XIcon style={{ width: 12, height: 12 }} strokeWidth={2.5} />
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ color: MUTED, padding: 40, textAlign: "center", background: "white", borderRadius: 14, border: `1px solid ${BORDER}` }}>
          Loading…
        </div>
      ) : !hasData ? (
        <div style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 24 }}>
          <EmptyState
            icon={<BarChart3 style={{ width: 32, height: 32, color: MUTED }} />}
            title={`No bookings yet in ${rangeLabel}`}
            body="Once patients are booked in this month, completion and client-type breakdowns will appear here."
          />
        </div>
      ) : (
        <>
          {/* Registrations per day — per staff, horizontal scroll so 5+ cards
              stay one row wide on mobile and desktop */}
          <div style={{
            display: "flex",
            alignItems: "stretch",
            overflowX: "auto",
            gap: 16,
            paddingBottom: 8,
            scrollSnapType: "x proximity",
            WebkitOverflowScrolling: "touch",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            boxSizing: "border-box",
          }}>
            {(staffFirst ? [staffFirst] : staffShorts).map((staffKey) => {
              const days = (stats?.daily_registrations_by_staff || {})[staffKey] || [];
              const outreachDays = (stats?.outreach_days_by_staff || {})[staffKey] || 0;
              const travelDays = (stats?.travel_days_by_staff || {})[staffKey] || 0;
              const offDays = (stats?.off_days_by_staff || {})[staffKey] || 0;
              const outreachHours = (stats?.outreach_hours_by_staff || {})[staffKey] || 0;
              const adminHours   = (stats?.admin_hours_by_staff   || {})[staffKey] || 0;
              const drivingHours = (stats?.driving_hours_by_staff || {})[staffKey] || 0;
              const pendingCount = (stats?.pending_by_staff || {})[staffKey] || 0;
              const notQualifiedCount = (stats?.not_qualified_by_staff || {})[staffKey] || 0;
              const replacementCount = (stats?.replacement_by_staff || {})[staffKey] || 0;
              return (
                // Phones: full container width so the card matches the
                // sections above/below. Desktop: fixed 300px so several
                // staff cards sit side by side. Scroll-snap swiping works
                // either way.
                <div key={staffKey} style={{ flex: isNarrow ? "0 0 100%" : "0 0 300px", scrollSnapAlign: "start", display: "flex", flexDirection: "column" }}>
                <RegPerDayChart
                  title={staffKey}
                  days={days}
                  outreachDays={outreachDays}
                  travelDays={travelDays}
                  offDays={offDays}
                  outreachHours={outreachHours}
                  adminHours={adminHours}
                  drivingHours={drivingHours}
                  pendingCount={pendingCount}
                  notQualifiedCount={notQualifiedCount}
                  replacementCount={replacementCount}
                  onGoToOutreach={onGoToOutreach}
                  onPendingClick={() => onSegmentClick(
                    `${staffKey} · Hold clients`,
                    { staff: staffKey, status: "hold", include_holds: 1, _reg_scope: 1, _row_mode: "hold",
                      reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                  onNotQualifiedClick={() => onSegmentClick(
                    `${staffKey} · Not qualified`,
                    { staff: staffKey, status: "not_qualified", include_holds: 1, _reg_scope: 1, _row_mode: "not_qualified",
                      reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                  onReplacementClick={() => onSegmentClick(
                    `${staffKey} · Replacement clients`,
                    { staff: staffKey, client_type: "replacement", _reg_scope: 1, _row_mode: "replacement",
                      reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                  onScheduledClick={() => onSegmentClick(
                    `${staffKey} · Qualified clients`,
                    { staff: staffKey, _reg_scope: 1, _exclude_holds_and_replacements: 1, _row_mode: "qualified",
                      reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                  drugOrder={(medications || []).map((m) => m.name)}
                  dispensedByDrug={(stats?.dispensed_by_drug_by_staff || {})[staffKey] || {}}
                  pendingDispensed={(stats?.pending_dispensed_by_staff || {})[staffKey] || 0}
                  caseStages={caseStages}
                  stageCounts={(stats?.stage_counts_by_staff || {})[staffKey] || {}}
                  onStageClick={(key, label) => onSegmentClick(
                    `${staffKey} · ${label}`,
                    { staff: staffKey, stage: key, include_holds: 1, _row_mode: "hcn",
                      _reg_scope: 1, reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                  onDrugClick={(drug) => onSegmentClick(
                    `${staffKey} · ${drug} dispensed`,
                    { staff: staffKey, medication: drug, _any_dispensed: 1, include_holds: 1, _row_mode: "hcn",
                      _reg_scope: 1, reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                  onPendingDispensedClick={() => onSegmentClick(
                    `${staffKey} · Pending`,
                    { staff: staffKey, med_stage_none: 1, include_holds: 1, _row_mode: "hcn",
                      _reg_scope: 1, reg_from: stats?.range?.start, reg_to: stats?.range?.end_exclusive },
                  )}
                />
                </div>
              );
            })}
          </div>

          {/* Big numbers */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: 16,
          }}>
            <StatCard
              label="Total appointments"
              value={t.total}
              accent={DARK_GREEN}
              testId="consult-stats-total"
            />
            <StatCard
              label="Completion rate"
              value={`${p.completed}%`}
              sub={`${t.completed} of ${t.total} completed`}
              accent={COMPLETED_COL}
              testId="consult-stats-completed"
            />
            <StatCard
              label="Missed rate"
              value={`${p.missed}%`}
              sub={`${t.missed} no-show / cancelled`}
              accent={MISSED_COL}
              testId="consult-stats-missed"
            />
            <StatCard
              label="New client rate"
              value={`${p.new}%`}
              sub={`${t.new} new · ${t.rescheduled} rescheduled`}
              accent={NEW_COL}
              testId="consult-stats-new"
            />
          </div>

          {/* Individual breakdown — per-staff registration totals for the
              selected month. Reads `stats.total_registrations_by_staff`
              which already applies the Christian→George July-2026 cutoff.
              Drill-down filters by REGISTRATION date within the month
              (not appointment date), matching the bar count. */}
          {(() => {
            const byStaffAll = stats?.total_registrations_by_staff || {};
            // staff scope: only their own segment
            const byStaff = staffFirst ? { [staffFirst]: byStaffAll[staffFirst] || 0 } : byStaffAll;
            const totalReg = Object.values(byStaff).reduce((sum, n) => sum + n, 0);
            const pctOf = (n) => (totalReg > 0 ? Math.round((n / totalReg) * 100) : 0);
            const r = stats?.range || {};
            const regFilter = (name) => ({
              staff: name,
              reg_from: r.start,
              reg_to: r.end_exclusive,
              _reg_scope: 1,  // signal to openStatsDetail: skip appt-date from/to
              include_holds: 1,  // Individual count includes holds; show them on drill-down
            });
            return (
              <BreakdownBar
                title="Individual breakdown"
                testId="consult-stats-individual-bar"
                onSegmentClick={onSegmentClick}
                segments={staffShorts
                  .map((s, i) => ({ label: s, color: STAFF_PALETTE[i % STAFF_PALETTE.length] }))
                  .filter((s) => !staffFirst || s.label === staffFirst)
                  .map((s) => ({ label: s.label, count: byStaff[s.label] || 0, pct: pctOf(byStaff[s.label] || 0), color: s.color, filter: regFilter(s.label) }))}
              />
            );
          })()}

          {/* Every bar below is scoped to REGISTRATION date so August's
              numbers reflect patients *registered* in August, regardless
              of when their appointment lands. Holds + not-qualified are
              always included since they may not have an appt date. */}
          {(() => { return null; })()}

          {/* Status breakdown bar */}
          <BreakdownBar
            title="Status breakdown"
            testId="consult-stats-status-bar"
            onSegmentClick={onSegmentClick}
            segments={[
              { label: "Qualified", count: t.scheduled, pct: p.scheduled, color: DARK_GREEN, filter: { status: "scheduled", _row_mode: "qualified", ...RS } },
              { label: "Completed", count: t.completed, pct: p.completed, color: COMPLETED_COL, filter: { status: "completed", ...RS } },
              { label: "Missed", count: t.missed, pct: p.missed, color: MISSED_COL, filter: { status: "missed", ...RS } },
              // The three "unqualified" statuses do not count toward the
              // 100% split. They still render as chips (with just the raw
              // count) so the number stays visible on the chart.
              { label: "Hold", count: t.hold || 0, pct: p.hold || 0, color: "#E58A2E", filter: { status: "hold", _row_mode: "hold", ...RS }, noPct: true },
              { label: "Not qualified", count: t.not_qualified || 0, pct: p.not_qualified || 0, color: "#B4351B", filter: { status: "not_qualified", _row_mode: "not_qualified", ...RS }, noPct: true },
              { label: "N/A", count: t.not_required || 0, pct: p.not_required || 0, color: MUTED, filter: { status: "not_required", ...RS }, noPct: true },
            ]}
          />

          {/* Client type breakdown bar */}
          <BreakdownBar
            title="Client type breakdown"
            testId="consult-stats-clienttype-bar"
            onSegmentClick={onSegmentClick}
            segments={[
              { label: "New", count: t.new, pct: p.new, color: NEW_COL, filter: { client_type: "new", ...RS } },
              { label: "Rescheduled", count: t.rescheduled, pct: p.rescheduled, color: RESCHED_COL, filter: { client_type: "rescheduled", ...RS } },
              { label: "Replacement", count: t.replacement || 0, pct: p.replacement || 0, color: "#4A6FB0", filter: { client_type: "replacement", _row_mode: "replacement", ...RS } },
            ]}
          />

          {/* Referral source breakdown (Outreach vs Pharmacy) */}
          <BreakdownBar
            title="Referral source"
            testId="consult-stats-referraltype-bar"
            onSegmentClick={onSegmentClick}
            segments={[
              { label: "Outreach", count: t.outreach, pct: p.outreach, color: DARK_GREEN, filter: { referral_source: "outreach", ...RS } },
              { label: "Pharmacy", count: t.pharmacy, pct: p.pharmacy, color: "#4A7C9B", filter: { referral_source: "pharmacy", ...RS } },
            ]}
          />

          {/* Referral sent breakdown (Yes / Review / Pending / Not required) */}
          <BreakdownBar
            title="Referral sent"
            testId="consult-stats-referralsent-bar"
            onSegmentClick={onSegmentClick}
            segments={[
              { label: "Yes",          count: t.referral_completed || 0,     pct: p.referral_completed || 0,     color: COMPLETED_COL,    filter: { referral: "completed",    ...RS } },
              { label: "Review",       count: t.referral_review || 0,        pct: p.referral_review || 0,        color: "#4A6FB0",        filter: { referral: "review",       ...RS } },
              { label: "Pending",      count: t.referral_pending || 0,       pct: p.referral_pending || 0,       color: REF_PENDING_COL,  filter: { referral: "pending",      ...RS } },
              { label: "N/A", count: t.referral_not_required || 0,  pct: p.referral_not_required || 0,  color: NOT_REQUIRED_COL, filter: { referral: "not_required", ...RS } },
            ]}
          />

          {/* Health card breakdown */}
          <BreakdownBar
            title="Health card"
            testId="consult-stats-healthcard-bar"
            onSegmentClick={onSegmentClick}
            segments={[
              { label: "Yes", count: t.health_card_yes, pct: p.health_card_yes, color: COMPLETED_COL, filter: { health_card_status: "yes", ...RS } },
              { label: "Unknown", count: t.health_card_unknown, pct: p.health_card_unknown, color: "#6B7280", filter: { health_card_status: "unknown", ...RS } },
              { label: "No", count: t.health_card_no, pct: p.health_card_no, color: REF_PENDING_COL, filter: { health_card_status: "no", ...RS } },
              { label: "Recovered", count: t.health_card_recovered, pct: p.health_card_recovered, color: NEW_COL, filter: { health_card_status: "recovered", ...RS } },
            ]}
          />

          {/* Registration timing — one segment per registration month */}
          <BreakdownBar
            title="Consultation month (registration timing)"
            testId="consult-stats-regtiming-bar"
            onSegmentClick={onSegmentClick}
            segments={buildRegMonthSegments(stats?.registration_months)}
          />

          {/* Medications — one segment per medication name (sorted desc),
              plus Hold, Not qualified and Replacement pseudo-segments so
              those records still show up on the graph without inflating
              new-prescription counts. */}
          <BreakdownBar
            title="Medications"
            testId="consult-stats-medications-bar"
            onSegmentClick={onSegmentClick}
            segments={buildMedicationSegments(stats?.medications, RS, {
              hold: { count: t.hold || 0, pct: p.hold || 0 },
              notQualified: { count: t.not_qualified || 0, pct: p.not_qualified || 0 },
              replacement: { count: t.replacement || 0, pct: p.replacement || 0 },
            })}
          />
        </>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background: "white", borderRadius: 14, border: `1px solid ${BORDER}`,
        padding: "18px 20px", position: "relative", overflow: "hidden",
      }}
    >
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3, background: accent,
      }} />
      <p style={{ ...labelStyle, fontSize: 11, marginBottom: 6 }}>{label}</p>
      <div style={{
        fontFamily: "'Work Sans', system-ui, sans-serif",
        fontSize: 32, fontWeight: 700, color: accent, letterSpacing: "-0.01em", lineHeight: 1,
      }}>
        {value}
      </div>
      {sub && (
        <p style={{ fontSize: 12, color: MUTED, margin: "8px 0 0", lineHeight: 1.4 }}>
          {sub}
        </p>
      )}
    </div>
  );
}

function BreakdownBar({ title, segments, testId, onSegmentClick }) {
  // Segments flagged `noPct: true` (e.g. Hold / Not qualified / Not
  // required on the Status breakdown) show a raw count only and are
  // excluded from the 100% split of the horizontal bar. We split them
  // into a "primary" row (percent-weighted) and "secondary" row
  // (counts only). All other bars have zero secondary segments so
  // their behaviour is unchanged.
  const primary = segments.filter((s) => !s.noPct);
  const secondary = segments.filter((s) => s.noPct);
  const primaryTotal = primary.reduce((s, x) => s + (x.count || 0), 0);
  const primaryWithPct = primary.map((s) => ({
    ...s,
    pct: primaryTotal ? Math.round(((s.count || 0) / primaryTotal) * 100) : 0,
  }));
  const clickable = (s) => onSegmentClick && s.filter && s.count > 0;
  const handleClick = (s) => {
    if (clickable(s)) onSegmentClick(s.label, s.filter);
  };
  const renderChip = (s, i, opts = {}) => {
    const isClickable = clickable(s);
    const showPct = !opts.noPct;
    return (
      <button
        key={`${s.label}-${i}`}
        type="button"
        data-testid={`${testId}-chip-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
        onClick={() => handleClick(s)}
        disabled={!isClickable}
        title={isClickable ? "View clients" : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 6,
          fontSize: 13, color: TEXT,
          background: "transparent", border: 0, padding: "2px 4px",
          borderRadius: 6,
          cursor: isClickable ? "pointer" : "default",
          fontFamily: "inherit", textAlign: "left",
          transition: "background 120ms ease",
          minWidth: 0,
        }}
        onMouseEnter={(e) => {
          if (isClickable) e.currentTarget.style.background = "#F4F3EF";
        }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
      >
        <span style={{ width: 10, height: 10, borderRadius: 3, background: s.color, display: "inline-block", flexShrink: 0 }} />
        <b style={{ whiteSpace: "nowrap" }}>{s.label}</b>
        <span style={{ color: MUTED, whiteSpace: "nowrap" }}>
          {showPct ? `${s.count} (${primaryTotal ? s.pct : 0}%)` : s.count}
        </span>
        {isClickable && (
          <ChevronRight style={{ width: 12, height: 12, color: MUTED, marginLeft: 2, flexShrink: 0 }} />
        )}
      </button>
    );
  };
  return (
    <div
      data-testid={testId}
      style={{ background: "white", borderRadius: 14, border: `1px solid ${BORDER}`, padding: 20 }}
    >
      <style>{`
        .breakdown-chips { margin-top: 12px; display: flex; flex-direction: column; gap: 8px; }
        .breakdown-chip-row {
          display: flex !important; flex-wrap: nowrap !important; gap: 14px;
          overflow-x: auto !important; overflow-y: hidden !important;
          -webkit-overflow-scrolling: touch;
          /* Bulletproof scrollbar hiding: multiple selectors + !important
             so any global rule (or iOS Safari) can't leak the native
             bar back over the chip text. */
          scrollbar-width: none !important;
          -ms-overflow-style: none !important;
        }
        .breakdown-chip-row::-webkit-scrollbar,
        .breakdown-chip-row::-webkit-scrollbar-track,
        .breakdown-chip-row::-webkit-scrollbar-thumb {
          display: none !important; width: 0 !important; height: 0 !important;
          background: transparent !important;
        }
        /* Each chip keeps its natural width — never shrink or wrap. If
           the row is wider than the screen, users can swipe/scroll. */
        .breakdown-chip-row > button {
          flex: 0 0 auto; white-space: nowrap;
        }
        .breakdown-chip-row > button > b,
        .breakdown-chip-row > button > span { white-space: nowrap; }
      `}</style>
      <p style={{ ...labelStyle, marginBottom: 12 }}>{title}</p>
      <div style={{
        display: "flex", height: 24, borderRadius: 8, overflow: "hidden",
        background: "#F9F8F6", border: `1px solid ${BORDER}`,
      }}>
        {primaryWithPct.map((s, i) => (
          s.pct > 0 ? (
            <button
              key={i}
              type="button"
              data-testid={`${testId}-seg-${s.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => handleClick(s)}
              disabled={!clickable(s)}
              title={
                clickable(s)
                  ? `${s.label}: ${s.count} (${s.pct}%) — click to view list`
                  : `${s.label}: ${s.count} (${s.pct}%)`
              }
              style={{
                width: `${s.pct}%`, background: s.color, border: 0, padding: 0,
                cursor: clickable(s) ? "pointer" : "default",
                transition: "opacity 120ms ease",
              }}
              onMouseEnter={(e) => { if (clickable(s)) e.currentTarget.style.opacity = "0.85"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
            />
          ) : null
        ))}
      </div>
      <div className="breakdown-chips">
        <div className="breakdown-chip-row hide-scrollbar">
          {primaryWithPct.map((s, i) => renderChip(s, i))}
          {secondary.map((s, i) => renderChip(s, `s-${i}`, { noPct: true }))}
        </div>
      </div>
    </div>
  );
}

const filterPill = (active) => ({
  background: active ? DARK_GREEN : "transparent",
  color: active ? "white" : MUTED,
  border: active ? "none" : `1px solid ${BORDER}`,
  borderRadius: 8, padding: "5px 10px",
  fontSize: 11.5, fontWeight: 600, cursor: "pointer",
  whiteSpace: "nowrap",
});

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <span style={{
        fontSize: 10, fontWeight: 700, color: LABEL_GREEN,
        letterSpacing: "0.18em", textTransform: "uppercase",
      }}>{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={testId}
          className="h-10 bg-white w-full text-[14px]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function FilterRow({ label, value, onChange, options, testPrefix }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 6,
    }}>
      <span style={{
        fontSize: 11, fontWeight: 700, color: LABEL_GREEN,
        letterSpacing: "0.18em", textTransform: "uppercase",
      }}>{label}</span>
      <div style={{ display: "flex", gap: 5, flexWrap: "nowrap" }}>
        {options.map((o) => (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            data-testid={`${testPrefix}-${o.v}`}
            style={filterPill(value === o.v)}
          >
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const monthNavBtn = {
  display: "flex", alignItems: "center", gap: 4,
  background: "white", color: TEXT,
  border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "8px 12px", fontSize: 13, fontWeight: 600, cursor: "pointer",
};

// Compact date-pill button for the stats custom-range picker.
const statsRangeBtn = (hasValue) => ({
  display: "inline-flex", alignItems: "center", gap: 5,
  background: "white",
  color: hasValue ? TEXT : MUTED,
  border: `1px solid ${BORDER}`,
  borderRadius: 8,
  padding: "6px 10px",
  fontSize: 12.5, fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
});

const iconBtn = {
  background: "transparent", border: `1px solid ${BORDER}`, borderRadius: 8,
  padding: "6px 8px", cursor: "pointer", color: MUTED,
  display: "flex", alignItems: "center", justifyContent: "center",
};

// -------------------------------------------------------------------------
// DobPicker — clean DOB selector with Month + Year Selects above a static
// calendar grid. Avoids the ugly native <select> that react-day-picker
// renders when captionLayout="dropdown".
// -------------------------------------------------------------------------
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function DobPicker({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const initial = value ? fromYMD(value) : new Date(1980, 0, 1);
  const [viewMonth, setViewMonth] = React.useState(initial);

  React.useEffect(() => {
    if (value) setViewMonth(fromYMD(value));
  }, [value]);

  const years = React.useMemo(() => {
    const arr = [];
    for (let y = currentYear; y >= 1920; y--) arr.push(y);
    return arr;
  }, [currentYear]);

  const setMonth = (mIndex) => {
    const d = new Date(viewMonth);
    d.setMonth(mIndex);
    setViewMonth(d);
  };
  const setYear = (yr) => {
    const d = new Date(viewMonth);
    d.setFullYear(yr);
    setViewMonth(d);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, width: 260 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
        <Select
          value={String(viewMonth.getMonth())}
          onValueChange={(v) => setMonth(Number(v))}
        >
          <SelectTrigger data-testid="consult-dob-month" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTH_NAMES.map((n, i) => (
              <SelectItem key={i} value={String(i)}>{n}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={String(viewMonth.getFullYear())}
          onValueChange={(v) => setYear(Number(v))}
        >
          <SelectTrigger data-testid="consult-dob-year" className="h-8 text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="max-h-64">
            {years.map((y) => (
              <SelectItem key={y} value={String(y)}>{y}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Calendar
        mode="single"
        month={viewMonth}
        onMonthChange={setViewMonth}
        selected={value ? fromYMD(value) : undefined}
        onSelect={(d) => { if (d) onChange(toYMD(d)); }}
        classNames={{ caption: "hidden" }}
        className="p-0"
      />
    </div>
  );
}

// -------------------------------------------------------------------------
// RegPerDayChart — vertical bar chart of consultations REGISTERED per day
// in the current stats month/range. Weekends are tinted. Clicking a bar
// drills down to that day's patients via the standard onSegmentClick prop.
// -------------------------------------------------------------------------
function LegendChip({ color, label, count, onClick, testId }) {
  const isClickable = !!onClick;
  return (
    <button
      type="button"
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
      data-testid={testId}
      title={`${label}: ${count}`}
      style={{
        display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 6,
        padding: "5px 10px", borderRadius: 8,
        background: "rgba(255,255,255,0.7)",
        border: `1px solid ${color}55`,
        color: TEXT, fontSize: 11, fontWeight: 600,
        fontFamily: "inherit",
        cursor: isClickable ? "pointer" : "default",
        opacity: (count === 0 && isClickable) ? 0.55 : 1,
        whiteSpace: "nowrap", minWidth: 0, overflow: "hidden",
      }}
    >
      <span style={{
        display: "inline-block", width: 8, height: 8,
        background: color, borderRadius: 2, flexShrink: 0,
      }} />
      <span style={{
        overflow: "hidden", textOverflow: "ellipsis",
      }}>{label}</span>
      <span style={{
        fontWeight: 800, color, flexShrink: 0,
        marginLeft: "auto",
        fontVariantNumeric: "tabular-nums",
      }}>{count}</span>
    </button>
  );
}

function RegPerDayChart({
  days, title, outreachDays, travelDays = 0, offDays = 0, outreachHours,
  adminHours = 0, drivingHours = 0,
  pendingCount = 0, notQualifiedCount = 0, replacementCount = 0,
  onPendingClick, onNotQualifiedClick, onReplacementClick, onScheduledClick,
  dispensedByDrug = {}, pendingDispensed = 0, stageCounts = {}, drugOrder = [], caseStages = [],
  onDrugClick, onPendingDispensedClick, onStageClick,
  onGoToOutreach,
}) {
  // Never return null — a staff member with no registrations this month
  // still needs the full card (zeros everywhere) so the row stays uniform.
  const hasOutreach = typeof outreachDays === "number" && outreachDays > 0;
  // Staff statistics (hours + conversion) — the numbers grid above the four
  // status boxes. (The per-day bar chart itself was removed per admin
  // request; these stay.)
  const total = (days || []).reduce((s, d) => s + (d.count || 0), 0);
  const workingDays = (outreachDays || 0) + (travelDays || 0);
  const hasWorkingDays = workingDays > 0;
  const avg = hasWorkingDays ? (total / workingDays).toFixed(2) : "—";
  // Backend sends `null` for hours when the staff has not marked the month
  // "Complete" on the Outreach panel. In that state we show "—" as a
  // reminder that the number isn't ready yet.
  const hoursReported = typeof outreachHours === "number" && outreachHours >= 0;
  const hoursLabel = hoursReported ? `${outreachHours.toFixed(1)} h` : "—";
  const hoursPerPatient = hoursReported && total > 0
    ? `${(outreachHours / total).toFixed(2)} h`
    : "—";

  // Dispensed-by-drug section data (drug list follows Manage medications order,
  // any drug with counts but not in the list appends at the end).
  const DRUG_COLORS = ["#1F4E79", "#2A9F92", "#7A5C9E", "#C0783A", "#8A5A00", "#4A6FB0"];
  const extraDrugs = Object.keys(dispensedByDrug || {}).filter((d) => !(drugOrder || []).includes(d));
  const drugRows = [...(drugOrder || []), ...extraDrugs];
  const staffSlug = (title || "").toLowerCase();

  const chartTestId = title
    ? `consult-stats-regperday-chart-${title.toLowerCase()}`
    : "consult-stats-regperday-chart";

  // Case-management row renderers — shared by the four group sections.
  const renderPendingRow = () => (
    <button
      key="__pending__"
      type="button"
      disabled={pendingDispensed === 0}
      onClick={pendingDispensed > 0 ? onPendingDispensedClick : undefined}
      data-testid={`consult-stats-dispensed-pending-${staffSlug}`}
      style={{
        display: "flex", alignItems: "center", gap: 8, width: "100%",
        padding: "7px 10px", borderRadius: 8, marginBottom: 6,
        border: "1px solid rgba(229,138,46,.4)", background: "#FDF4EA",
        fontSize: 12, fontWeight: 500, fontFamily: "inherit", color: TEXT,
        cursor: pendingDispensed > 0 ? "pointer" : "default",
        textAlign: "left",
      }}
    >
      <span style={{ width: 8, height: 8, borderRadius: 2, background: "#E58A2E", flexShrink: 0 }} />
      <span>Pending</span>
      <span style={{ marginLeft: "auto", fontWeight: 800, color: "#E58A2E", minWidth: 20, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pendingDispensed}</span>
      {/* chevron slot is always reserved so every count ends on the same axis */}
      <span style={{ fontSize: 10, color: MUTED, width: 12, textAlign: "center", visibility: pendingDispensed > 0 ? "visible" : "hidden" }}>›</span>
    </button>
  );
  const renderStageRow = (s) => {
    const count = (stageCounts || {})[s.key] || 0;
    const color = s.color || "#1F4E79";
    return (
      <button
        key={s.key}
        type="button"
        disabled={count === 0}
        onClick={count > 0 && onStageClick ? () => onStageClick(s.key, s.label) : undefined}
        data-testid={`consult-stats-dispensed-${s.key}-${staffSlug}`}
        style={{
          display: "flex", alignItems: "center", gap: 8, width: "100%",
          padding: "7px 10px", borderRadius: 8, marginBottom: 6,
          border: `1px solid ${color}66`, background: `${color}14`,
          fontSize: 12, fontWeight: 500, fontFamily: "inherit", color: TEXT,
          cursor: count > 0 ? "pointer" : "default",
          textAlign: "left",
        }}
      >
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
        <span>{s.label}</span>
        <span style={{ marginLeft: "auto", fontWeight: 800, color, minWidth: 20, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{count}</span>
        {/* chevron slot is always reserved so every count ends on the same axis */}
        <span style={{ fontSize: 10, color: MUTED, width: 12, textAlign: "center", visibility: count > 0 ? "visible" : "hidden" }}>›</span>
      </button>
    );
  };


  return (
    <div
      data-testid={chartTestId}
      style={{
        background: "white",
        borderRadius: 14,
        border: `1px solid ${BORDER}`,
        padding: 20,
        flex: 1,
        minHeight: "100%",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 6 }}>
        {title && (
          <p style={{ ...labelStyle, margin: 0, marginBottom: 2 }}>{title}</p>
        )}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
        }}>
          <p style={{
            fontSize: 12.5, color: MUTED, fontWeight: 500, margin: 0,
            letterSpacing: "0.02em",
          }}>
            Summary
          </p>
          {onGoToOutreach && (
            <button
              type="button"
              onClick={onGoToOutreach}
              data-testid={`consult-stats-regperday-set-outreach-${(title || "").toLowerCase()}`}
              style={{
                background: "transparent",
                border: `1px solid ${BORDER}`,
                borderRadius: 6, padding: "4px 8px",
                fontSize: 11, fontWeight: 600, color: LABEL_GREEN,
                cursor: "pointer", fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
            >
              {hasOutreach ? "Edit outreach days" : "Set outreach days"}
            </button>
          )}
        </div>
      </div>

      <div style={{
        display: "grid",
        // Two-line stat cells (LABEL above, value below) so long values
        // never truncate and every cell is the same size. 3 columns on
        // wide cards, 2 on narrow, using minmax to stay flexible.
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        columnGap: 10, rowGap: 12,
        fontSize: 12, color: MUTED, marginBottom: 14,
      }}>
        {[
          { label: "Total",       value: total },
          { label: "Avg / day",   value: avg,             title: "Total registrations ÷ (outreach days + travel days)" },
          { label: "Worked",      value: hoursLabel,      title: "Total hours worked this range" },
          { label: "Per patient", value: hoursPerPatient, title: "Total hours worked ÷ total consultations registered" },
          { label: "Work",        value: typeof outreachDays === "number" ? outreachDays : 0, title: "Work days this range" },
          { label: "Travel",      value: typeof travelDays === "number" ? travelDays : 0, title: "Travel days this range" },
          { label: "Off",         value: typeof offDays === "number" ? offDays : 0, title: "Off days this range" },
          { label: "Admin",       value: `${adminHours.toFixed(1)} h`,   color: "#8A5A00", title: "Admin hours are excluded from the hours-per-patient average." },
          { label: "Driving",     value: `${drivingHours.toFixed(1)} h`, color: "#1F4E79", title: "Driving hours are excluded from the hours-per-patient average." },
        ].map((row, i) => (
          <div
            key={i}
            title={row.title || undefined}
            style={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <div style={{
              fontSize: 10, fontWeight: 700, color: row.color || MUTED,
              textTransform: "uppercase", letterSpacing: "0.06em",
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
            }}>
              {row.label}
            </div>
            <div style={{
              fontSize: 14, fontWeight: 700, color: TEXT,
              whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              fontVariantNumeric: "tabular-nums",
            }}>
              {row.value}
            </div>
          </div>
        ))}
      </div>

      {/* Clickable legend — each swatch drills down to the underlying
          patients. 2×2 grid so full labels fit on every card width. */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 6, marginBottom: 6,
      }}>
        <LegendChip
          color={DARK_GREEN}
          label="Qualified"
          count={(days || []).reduce((s, d) => s + (d.count || 0), 0)}
          onClick={onScheduledClick || null}
          testId={`consult-stats-scheduled-${(title || "").toLowerCase()}`}
        />
        <LegendChip
          color="#E58A2E"
          label="Hold"
          count={pendingCount}
          onClick={pendingCount > 0 ? onPendingClick : null}
          testId={`consult-stats-pending-${(title || "").toLowerCase()}`}
        />
        <LegendChip
          color="#B4351B"
          label="Not qualified"
          count={notQualifiedCount}
          onClick={notQualifiedCount > 0 ? onNotQualifiedClick : null}
          testId={`consult-stats-not-qualified-${(title || "").toLowerCase()}`}
        />
        <LegendChip
          color="#4A6FB0"
          label="Replacement"
          count={replacementCount}
          onClick={replacementCount > 0 ? onReplacementClick : null}
          testId={`consult-stats-replacement-${(title || "").toLowerCase()}`}
        />
      </div>

      {/* Registrations chart + date axis removed per admin request —
       the card is just the four status boxes, then Case management. */}

      {/* Case-management pipeline: four always-expanded groups (Active /
          Not qualified / Reimbursement / Pharmacy), then per-drug Dispensed
          rows. A record counts only in its FURTHEST stage. */}
      {drugRows.length > 0 && (
        <div
          style={{ marginTop: 12, borderTop: `1px solid ${BORDER}`, paddingTop: 12 }}
          data-testid={`consult-stats-dispensed-${staffSlug}`}
        >
          <p style={{ fontSize: 10, fontWeight: 700, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", margin: "0 0 6px" }}>
            Case management
          </p>
          {/* Four always-expanded groups; each stage sits in the group the
              admin picked in Manage (custom stages default to Active).
              Pending lives in Active; empty groups are hidden. Pipeline
              order inside a group still follows the managed order. */}
          {CASE_STAGE_GROUPS.map((g) => {
            const groupStages = caseStages.filter((s) => (s.group || "active") === g.key);
            const isActive = g.key === "active";
            if (!isActive && groupStages.length === 0) return null;
            const subtotal = groupStages.reduce((sum, s) => sum + ((stageCounts || {})[s.key] || 0), 0)
              + (isActive ? pendingDispensed : 0);
            return (
              <div
                key={g.key}
                data-testid={`consult-stats-cmgroup-${g.key}-${staffSlug}`}
                style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "4px 8px 2px", marginBottom: 8 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 10px 6px" }}>
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: g.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: TEXT, lineHeight: 1 }}>{g.label}</span>
                  {/* Roomy square-ish badge like the original — but the digit
                      stays flush on the stage-count axis: symmetric padding,
                      badge edge lands 8px right of the axis (4px trailing
                      spacer instead of the 12px chevron slot). */}
                  <span
                    data-testid={`consult-stats-cmgroup-total-${g.key}-${staffSlug}`}
                    style={{
                      marginLeft: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center",
                      minWidth: 20, height: 20, padding: "0 8px", borderRadius: 8,
                      background: DARK_GREEN, border: `1px solid ${DARK_GREEN}`, color: "#fff",
                      fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1,
                    }}
                  >{subtotal}</span>
                  <span style={{ width: 4, flexShrink: 0 }} />
                </div>
                {isActive && renderPendingRow()}
                {groupStages.map(renderStageRow)}
              </div>
            );
          })}
          {/* Dispensed by drug — same group treatment as the sections above:
              header dot + title + total badge aligned to the count axis. */}
          <div
            data-testid={`consult-stats-cmgroup-drugs-${staffSlug}`}
            style={{ border: `1px solid ${BORDER}`, borderRadius: 10, padding: "4px 8px 2px" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "2px 10px 6px" }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: DARK_GREEN, flexShrink: 0 }} />
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: TEXT, lineHeight: 1 }}>Dispensed by drug</span>
              <span
                data-testid={`consult-stats-cmgroup-total-drugs-${staffSlug}`}
                style={{
                  marginLeft: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center",
                  minWidth: 20, height: 20, padding: "0 8px", borderRadius: 8,
                  background: DARK_GREEN, border: `1px solid ${DARK_GREEN}`, color: "#fff",
                  fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums", lineHeight: 1,
                }}
              >{drugRows.reduce((sum, d) => sum + ((dispensedByDrug || {})[d] || 0), 0)}</span>
              <span style={{ width: 4, flexShrink: 0 }} />
            </div>
          {drugRows.map((drug, i) => {
            const count = (dispensedByDrug || {})[drug] || 0;
            const color = DRUG_COLORS[i % DRUG_COLORS.length];
            return (
              <button
                key={drug}
                type="button"
                disabled={count === 0}
                onClick={count > 0 && onDrugClick ? () => onDrugClick(drug) : undefined}
                data-testid={`consult-stats-dispensed-${staffSlug}-${drug.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                style={{
                  display: "flex", alignItems: "center", gap: 8, width: "100%",
                  padding: "7px 10px", borderRadius: 8, marginBottom: 6,
                  border: `1px solid ${color}55`, background: "#fff",
                  fontSize: 12, fontWeight: 500, fontFamily: "inherit", color: TEXT,
                  cursor: count > 0 ? "pointer" : "default",
                  textAlign: "left",
                }}
              >
                <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{drug}</span>
                <span style={{ marginLeft: "auto", fontWeight: 800, color, minWidth: 20, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{count}</span>
                {/* chevron slot is always reserved so every count ends on the same axis */}
                <span style={{ fontSize: 10, color: MUTED, width: 12, textAlign: "center", visibility: count > 0 ? "visible" : "hidden" }}>›</span>
              </button>
            );
          })}
          </div>
        </div>
      )}
    </div>
  );
}



// ============================================================================
function ManageReasonsDialog({ open, type, onOpenChange, reasons, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRows(reasons.map((r) => ({ ...r, _draft: r.label })));
  }, [open, reasons]);

  const title = type === "not_qualified" ? "Manage not-qualified reasons" : "Manage hold reasons";

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.label) return;
    setBusy(true);
    try {
      await api.patch(`/reasons/${r.id}`, { label: r._draft.trim() });
      toast.success("Reason renamed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete reason "${r.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/reasons/${r.id}`);
      toast.success("Reason deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await api.post("/reasons", { type, label });
      setNewLabel("");
      toast.success("Reason added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consult-manage-reasons-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Add, rename or delete. Shared across all appointments with this status.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.length === 0 && (
            <div style={{ color: "#6B7A73", fontSize: 13, padding: 12, textAlign: "center" }}>
              No reasons yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`consult-reason-row-${r.id}`}
              style={{
                display: "flex", gap: 8, alignItems: "center",
                padding: "8px 10px", border: "1px solid #E5E2DC", borderRadius: 8,
              }}
            >
              <input
                value={r._draft}
                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _draft: e.target.value } : x))}
                onBlur={() => saveRow(r)}
                data-testid={`consult-reason-input-${r.id}`}
                style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 14, color: "#1C2B26", outline: "none" }}
              />
              {r._draft.trim() && r._draft.trim() !== r.label && (
                <button
                  type="button"
                  onClick={() => saveRow(r)}
                  data-testid={`consult-reason-save-${r.id}`}
                  title="Save"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2E7D5B", padding: 4 }}
                >
                  <Check style={{ width: 15, height: 15 }} strokeWidth={2.5} />
                </button>
              )}
              <button
                type="button"
                onClick={() => deleteRow(r)}
                data-testid={`consult-reason-delete-${r.id}`}
                title="Delete"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7A73", padding: 4 }}
              >
                <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="consult-new-reason-input"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder={type === "not_qualified" ? "Add reason (e.g. Recently treated)…" : "Add reason (e.g. Awaiting bloodwork)…"}
            style={{ flex: 1, height: 38, padding: "0 12px", fontSize: 14, borderRadius: 8, border: "1px solid #E5E2DC", background: "#fff" }}
          />
          <button
            type="button"
            data-testid="consult-new-reason-add"
            onClick={addRow}
            disabled={!newLabel.trim() || busy}
            style={{
              background: "#0F3B2E", color: "#fff", border: 0,
              borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 600,
              cursor: !newLabel.trim() || busy ? "default" : "pointer",
              opacity: !newLabel.trim() || busy ? 0.6 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="consult-manage-reasons-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: "#0F3B2E", color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


// ============================================================================
// Case-management stages manager — add / rename / delete. The server BLOCKS
// deletion while any client file ticks the stage and returns those files;
// we list them and let the admin open each record to edit its stage first.
function ManageCaseStagesDialog({ open, onOpenChange, stages, onChanged, onOpenFile }) {
  const [rows, setRows] = useState([]);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#1F4E79");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null); // { stage, message, files }

  useEffect(() => {
    if (open) {
      setRows(stages.map((s) => ({ ...s, _draft: s.label })));
      setBlocked(null);
    }
  }, [open, stages]);

  const renameRow = async (r) => {
    const label = (r._draft || "").trim();
    if (!label || label === r.label) return;
    setBusy(true);
    try {
      await api.patch(`/case-stages/${r.id}`, { label });
      toast.success("Stage renamed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  // Which of the four Stats-card groups (Active / Not qualified /
  // Reimbursement / Pharmacy) this stage appears under.
  const setGroup = async (r, group) => {
    if ((r.group || "active") === group) return;
    setBusy(true);
    try {
      await api.patch(`/case-stages/${r.id}`, { group });
      setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, group } : x)));
      toast.success("Group updated");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete "${r.label}" from the case-management list?`)) return;
    setBusy(true);
    setBlocked(null);
    try {
      await api.delete(`/case-stages/${r.id}`);
      toast.success(`"${r.label}" deleted`);
      onChanged();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.files) {
        setBlocked({ stage: r.label, message: d.message, files: d.files });
      } else {
        toast.error(typeof d === "string" ? d : "Delete failed");
      }
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const label = newLabel.trim();
    if (!label) return;
    setBusy(true);
    try {
      await api.post("/case-stages", { label, color: newColor });
      setNewLabel("");
      toast.success("Stage added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  // Move a stage up/down in the pipeline order and persist immediately —
  // this order drives the record dialog AND the Stats case-management rows.
  const moveRow = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
    setBusy(true);
    try {
      await api.post("/case-stages/reorder", { ids: next.map((r) => r.id) });
      toast.success("Order saved");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the order");
      onChanged(); // reload to resync with the server
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consult-manage-stages-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage case-management stages</DialogTitle>
          <DialogDescription>
            Add, rename, re-order or delete. The order here is the pipeline order everywhere (record dialog, Stats rows). Deleting is blocked while client files still use the stage.
          </DialogDescription>
        </DialogHeader>

        {blocked && (
          <div data-testid="consult-stage-blocked" style={{ background: "#FBEAE5", border: "1px solid #E9BFB1", borderRadius: 10, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#B4351B" }}>{blocked.message}</div>
            <div style={{ fontSize: 12, color: "#B4351B", marginTop: 4 }}>Open each file and change its Case management stage, then delete again:</div>
            <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
              {blocked.files.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`consult-stage-blocked-file-${f.id}`}
                  onClick={() => onOpenFile(f.id)}
                  style={{ display: "flex", gap: 8, alignItems: "center", background: "#fff", border: "1px solid #E9BFB1", borderRadius: 8, padding: "6px 10px", fontSize: 12, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}
                >
                  <span style={{ flex: 1, fontWeight: 700, color: "#1F4E79", textDecoration: "underline" }}>{f.patient_name}</span>
                  <span style={{ color: "#6B7A73" }}>{f.date || "no date"}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`consult-stage-row-${r.key}`}
              style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 10px", border: "1px solid #E5E2DC", borderRadius: 8, background: "#fff" }}
            >
              <span style={{ width: 10, height: 10, borderRadius: 3, background: r.color, flexShrink: 0 }} />
              {/* Reorder — display order IS pipeline order */}
              <span style={{ display: "inline-flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                <button
                  type="button"
                  aria-label="Move up"
                  data-testid={`consult-stage-up-${r.key}`}
                  onClick={() => moveRow(idx, -1)}
                  disabled={busy || idx === 0}
                  style={{ background: "none", border: "1px solid #E5E2DC", borderRadius: 4, width: 20, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: idx === 0 ? "default" : "pointer", opacity: idx === 0 ? 0.35 : 1, padding: 0, fontFamily: "inherit", lineHeight: 1 }}
                >
                  <ChevronUp style={{ width: 11, height: 11 }} strokeWidth={2.5} />
                </button>
                <button
                  type="button"
                  aria-label="Move down"
                  data-testid={`consult-stage-down-${r.key}`}
                  onClick={() => moveRow(idx, 1)}
                  disabled={busy || idx === rows.length - 1}
                  style={{ background: "none", border: "1px solid #E5E2DC", borderRadius: 4, width: 20, height: 14, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: idx === rows.length - 1 ? "default" : "pointer", opacity: idx === rows.length - 1 ? 0.35 : 1, padding: 0, fontFamily: "inherit", lineHeight: 1 }}
                >
                  <ChevronDown style={{ width: 11, height: 11 }} strokeWidth={2.5} />
                </button>
              </span>
              <input
                value={r._draft}
                data-testid={`consult-stage-rename-${r.key}`}
                onChange={(e) => setRows((rs) => rs.map((x, i) => (i === idx ? { ...x, _draft: e.target.value } : x)))}
                onBlur={() => renameRow(r)}
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                style={{ flex: 1, minWidth: 0, height: 32, padding: "0 8px", fontSize: 13, border: "1px solid #E5E2DC", borderRadius: 6, fontFamily: "inherit" }}
              />
              <select
                value={r.group || "active"}
                data-testid={`consult-stage-group-${r.key}`}
                onChange={(e) => setGroup(r, e.target.value)}
                disabled={busy}
                title="Stats card group"
                style={{ height: 32, fontSize: 11, padding: "0 4px", border: "1px solid #E5E2DC", borderRadius: 6, background: "#FAF9F6", fontFamily: "inherit", color: "#1C1B1A", flexShrink: 0 }}
              >
                {CASE_STAGE_GROUPS.map((g) => (
                  <option key={g.key} value={g.key}>{g.label}</option>
                ))}
              </select>
              <button
                type="button"
                data-testid={`consult-stage-delete-${r.key}`}
                onClick={() => deleteRow(r)}
                disabled={busy}
                style={{ background: "none", border: "1px solid #E9BFB1", color: "#B4351B", borderRadius: 6, padding: "5px 10px", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
              >
                Delete
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <input
            value={newLabel}
            data-testid="consult-stage-new-label"
            onChange={(e) => setNewLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder="New stage name…"
            style={{ flex: 1, minWidth: 0, height: 36, padding: "0 10px", fontSize: 13, border: "1px solid #E5E2DC", borderRadius: 8, fontFamily: "inherit" }}
          />
          <input
            type="color"
            value={newColor}
            data-testid="consult-stage-new-color"
            onChange={(e) => setNewColor(e.target.value)}
            title="Stage color"
            style={{ width: 36, height: 36, padding: 2, border: "1px solid #E5E2DC", borderRadius: 8, background: "#fff", cursor: "pointer" }}
          />
          <Button onClick={addRow} disabled={busy} data-testid="consult-stage-add" className="h-9 px-4" style={{ background: DARK_GREEN, color: "white" }}>
            Add
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ManageMedicationsDialog({ open, onOpenChange, medications, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setRows(medications.map((m) => ({ ...m, _draft: m.name })));
  }, [open, medications]);

  const saveRow = async (r) => {
    if (!r._draft.trim() || r._draft.trim() === r.name) return;
    setBusy(true);
    try {
      await api.patch(`/medications/${r.id}`, { name: r._draft.trim() });
      toast.success("Medication renamed");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteRow = async (r) => {
    if (!window.confirm(`Delete medication "${r.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/medications/${r.id}`);
      toast.success("Medication deleted");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const addRow = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post("/medications", { name });
      setNewName("");
      toast.success("Medication added");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="consult-manage-meds-dialog" className="max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage medications</DialogTitle>
          <DialogDescription>
            Add, rename or delete. Shared across all appointments.
          </DialogDescription>
        </DialogHeader>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
          {rows.length === 0 && (
            <div style={{ color: "#6B7A73", fontSize: 13, padding: 12, textAlign: "center" }}>
              No medications yet — add one below.
            </div>
          )}
          {rows.map((r, idx) => (
            <div
              key={r.id}
              data-testid={`consult-med-row-${r.id}`}
              style={{
                display: "flex", gap: 8, alignItems: "center",
                padding: "8px 10px", border: "1px solid #E5E2DC", borderRadius: 8,
              }}
            >
              <input
                value={r._draft}
                onChange={(e) => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _draft: e.target.value } : x))}
                onBlur={() => saveRow(r)}
                data-testid={`consult-med-input-${r.id}`}
                style={{ flex: 1, border: 0, padding: 0, background: "transparent", fontSize: 14, color: "#1C2B26", outline: "none" }}
              />
              {r._draft.trim() && r._draft.trim() !== r.name && (
                <button
                  type="button"
                  onClick={() => saveRow(r)}
                  data-testid={`consult-med-save-${r.id}`}
                  title="Save"
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#2E7D5B", padding: 4 }}
                >
                  <Check style={{ width: 15, height: 15 }} strokeWidth={2.5} />
                </button>
              )}
              <button
                type="button"
                onClick={() => deleteRow(r)}
                data-testid={`consult-med-delete-${r.id}`}
                title="Delete"
                style={{ background: "none", border: "none", cursor: "pointer", color: "#6B7A73", padding: 4 }}
              >
                <Trash2 style={{ width: 15, height: 15 }} strokeWidth={2} />
              </button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <input
            data-testid="consult-new-med-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") addRow(); }}
            placeholder="Add new medication…"
            style={{ flex: 1, height: 38, padding: "0 12px", fontSize: 14, borderRadius: 8, border: "1px solid #E5E2DC", background: "#fff" }}
          />
          <button
            type="button"
            data-testid="consult-new-med-add"
            onClick={addRow}
            disabled={!newName.trim() || busy}
            style={{
              background: "#0F3B2E", color: "#fff", border: 0,
              borderRadius: 8, padding: "0 16px", fontSize: 13, fontWeight: 600,
              cursor: !newName.trim() || busy ? "default" : "pointer",
              opacity: !newName.trim() || busy ? 0.6 : 1,
            }}
          >
            Add
          </button>
        </div>

        <div style={{ marginTop: 18 }}>
          <button
            type="button"
            data-testid="consult-manage-meds-done"
            onClick={() => onOpenChange(false)}
            style={{
              width: "100%", background: "#0F3B2E", color: "#fff", border: 0,
              borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Done
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
